/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AppState } from 'src/app/store/app.reducer';
import * as DeviceActions from './device.actions';
import * as SiteActions from 'src/app/store/site/site.actions';
import { DeviceService } from 'src/app/services/device.service';
import { DeviceStatus, fillDeviceStatus } from 'src/app/model/device-status';
import { EDeviceCategory } from 'src/app/conf/device-categories';
import { supportCommands } from 'src/app/model/device';
import { UserService } from 'src/app/services/user.service';
import { BoardUsersService } from 'src/app/services/board-users.service';
import { ProfilesService } from 'src/app/services/profiles.service';
import { needBUResync } from 'src/app/model/board-users-info';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import { partialUpdateFromEvent } from 'src/app/model/api/ws-event-message';
import { Router } from '@angular/router';
import * as NotificationsActions from '../notifications/notifications.actions';
import * as AnalyticsActions from 'src/app/store/analytics/analytics.actions';
import { selectCurrentDeviceModel } from './device.selector';

@Injectable()
export class DeviceEffects {

    addDevice$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addDevice),
        switchMap((action) => this.deviceService.addDevice(action.device)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.addDeviceSuccess({
                            device: { ...action.device, Id: resp.Data.Id },
                            message: this.translate.instant('DEVICE_ADD_SUCCESS')
                        });
                    }
                    if (resp.ErrorCode === 403) {
                        return DeviceActions.addDeviceFailed({ errorMessage: this.translate.instant('DEVICE_ADD_ERROR_SUBGAT') });
                    }
                    if (resp.ErrorMessage === 'Cannot create gateway, keycode already used') {
                        return DeviceActions.addDeviceFailed({ errorMessage: this.translate.instant('DEVICE_ADD_ERROR_ALREADY_USED') });
                    }
                    if (resp.ErrorMessage === 'error duplicated automation') {
                        return DeviceActions.addDeviceFailed({ errorMessage: this.translate.instant('DEVICE_ADD_ERROR_DUPLICATE_CRP_AUTOMATION'), errorDuplicateCrp: true });
                    }
                    return DeviceActions.addDeviceFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                }),
                catchError(error => of(DeviceActions.addDeviceFailed({ errorMessage: '' })))
            )
        )
    ));

    addDeviceSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addDeviceSuccess),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventDevice({ eventName: 'add_device_completed', params: { page_title: 'Device' } }));
            }, 2000);
        }),
    ), { dispatch: false });

    addDeviceFailedAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addDeviceFailed),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventDevice({ eventName: 'add_device_failed', params: { page_title: 'Device' } }));
            }, 2000);
        }),
    ), { dispatch: false });

    addDeviceSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addDeviceSuccess),
        switchMap((action) => [
            SiteActions.getSite({ siteId: action.device.SiteId }),
            DeviceActions.initDevice({ device: action.device, terminals: action.device.Terminals })
        ])
    ));

    editDevice$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editDevice),
        switchMap((action) => this.deviceService.editDevice(action.device)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editDeviceSuccess({
                            device: action.device,
                            // eslint-disable-next-line max-len
                            message: this.translate.instant('DEVICE_EDIT_SUCCESS', { name: action.device.Name.length > 20 ? action.device.Name.slice(0, 20) + ' ...' : action.device.Name })
                        });
                    }
                    if (resp.ErrorMessage === 'error duplicated automation') {
                        return DeviceActions.editDeviceFailed({ errorMessage: this.translate.instant('DEVICE_ADD_ERROR_DUPLICATE_CRP_AUTOMATION'), errorDuplicateCrp: true });
                    }
                    return DeviceActions.editDeviceFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.editDeviceFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteDevice$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteDevice),
        switchMap((action) => this.deviceService.deleteDevice(action.deviceId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteDeviceSuccess({
                            id: resp.Data.Id,
                            message: this.translate.instant('DELETE_DEVICE_SUCCESS'),
                        });
                    } else {
                        return DeviceActions.deleteDeviceFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteDeviceFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteDeviceSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteDeviceSuccess),
        withLatestFrom(
            this.store.select('site', 'currentSite'),
        ),
        switchMap(([action, site]) => {
            return of(SiteActions.getSite({ siteId: site.Id }));
        })
    ));

    /*deleteDeviceSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteDeviceSuccess),
        withLatestFrom(
            this.store.select('site', 'currentSite'),
        ),
        switchMap(([action, site]) => [
            SiteActions.getSite({ siteId: site.Id }),
        ])
    ));*/

    deleteSharedDevice$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteSharedDevice),
        switchMap((action) => this.deviceService.removeShareDevice(action.deviceId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteSharedDeviceSuccess({ message: this.translate.instant('DELETE_SHARED_DEVICE_SUCCESS') });
                    } else {
                        return DeviceActions.deleteSharedDeviceFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteSharedDeviceFailed({ errorMessage: error.message })))
            )
        )
    ));

    /*closeAddDialog$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addDeviceSuccess),
        tap(() => this.dialog.closeAll()), //TODO REPLACEEEEEEEEEEEE
    ), { dispatch: false });*/

    getDevicesStatus$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getDevicesStatus),
        map(action => action.deviceIds),
        withLatestFrom(
            this.store.select('device', 'gateStates'),
            this.store.select('device', 'gateErrors'),
        ),
        switchMap(([deviceIds, gateStates, gateErrors]) => this.deviceService.getDevicesStatus(deviceIds)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const devicesStatus = resp.Data.map(status => {
                            const deviceStatus: DeviceStatus = fillDeviceStatus(status, gateStates, gateErrors);
                            return deviceStatus;
                        });
                        return DeviceActions.getDevicesStatusSuccess({ devicesStatus });
                    } else {
                        return DeviceActions.getDevicesStatusFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.getDevicesStatusFailed({ errorMessage: error.message })))
            )
        )
    ));

    deviceStateUpdate$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deviceStateUpdate),
        withLatestFrom(
            this.store.select('device', 'gateStates'),
            this.store.select('device', 'gateErrors'),
        ),
        switchMap(([action, gateStates, gateErrors]) => {
            const patch = partialUpdateFromEvent(action.event, gateStates, gateErrors);
            return of(DeviceActions.deviceStateUpdateSuccess({
                deviceId: action.deviceId,
                message: action.event.Message,
                notificationLevel: patch.Errored ? NotificationLevel.Error : NotificationLevel.Info,
                patch
            }));
        })
    ));

    updateCurrentDeviceStatus$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.updateCurrentDeviceStatus, DeviceActions.getCurrentDeviceSuccess, DeviceActions.initDeviceSuccess),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
            this.store.select('device', 'gateStates'),
            this.store.select('device', 'gateErrors'),
        ),
        switchMap(([action, device, gateStates, gateErrors]) => this.deviceService.getDevicesStatus([device.Id])
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const deviceStatus: DeviceStatus = fillDeviceStatus(resp.Data[0], gateStates, gateErrors);
                        return DeviceActions.updateCurrentDeviceStatusSuccess({ deviceStatus });
                    } else {
                        return DeviceActions.updateCurrentDeviceStatusFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.updateCurrentDeviceStatusFailed({ errorMessage: error.message })))
            )
        )
    ));

    getGateUpdateData$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getGateStatesSuccess, DeviceActions.getGateErrorsSuccess
        ),
        withLatestFrom(
            this.store.select('device', 'currentDeviceStatus'),
            this.store.select('device', 'gateStates'),
            this.store.select('device', 'gateErrors'),
        ),
        switchMap(([, currentDeviceStatus, gateStates, gateErrors]) => {
            if (currentDeviceStatus === undefined || gateStates === undefined || gateErrors === undefined) {
                return of({ type: 'UNDEFINED_OBJ' });
            }
            const deviceStatus: DeviceStatus = fillDeviceStatus(currentDeviceStatus, gateStates, gateErrors);
            return of(DeviceActions.updateCurrentDeviceStatusSuccess({ deviceStatus }));
        })
    ));

    getDevice$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getCurrentDevice),
        withLatestFrom(this.store.select('user', 'currentUser')),
        switchMap(([action, currentUser]) => this.deviceService.getDevice(action.deviceId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const device = resp.Data;
                        device.CurrentlyOwned = device.OwnerId === currentUser.Id;
                        return DeviceActions.getCurrentDeviceSuccess({ device });
                    }
                    return DeviceActions.getCurrentDeviceFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getCurrentDeviceFailed({ errorMessage: '' })))
            )
        )
    ));

    navigateAway$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getCurrentDeviceFailed),
        tap(() => {
            this.router.navigate(['/']);
        })
    ), { dispatch: false });

    getDeviceSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getCurrentDeviceSuccess),
        withLatestFrom(
            this.store.select('user', 'currentUser'),
            this.store.select('user', 'currentTechnician'),
        ),
        switchMap(([action, currentUser, currentTechnician]) => {
            let lang = currentUser.Language;
            if (currentTechnician) {
                lang = currentTechnician.Language;
            }
            return this.deviceService.getDeviceModel(action.device.ModelId, lang)
                .pipe(
                    map(resp => {
                        if (resp.Success) {
                            return DeviceActions.getDeviceModelSuccess({ deviceModel: resp.Data });
                        }
                        return DeviceActions.getDeviceModelFailed({ errorMessage: resp.ErrorMessage });
                    }),
                    catchError(error => of(DeviceActions.getDeviceModelFailed({ errorMessage: error.message })))
                );
        })
    ));

    getDeviceModel$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getDeviceModel),
        switchMap((action) => this.deviceService.getDeviceModel(action.modelId, action.language)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.getDeviceModelSuccess({ deviceModel: resp.Data });
                    }
                    return DeviceActions.getDeviceModelFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getDeviceModelFailed({ errorMessage: error.message })))
            )
        )
    ));

    getNewDeviceModels$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getNewDeviceModels),
        switchMap((action) => this.deviceService.getNewDeviceModels(action.categoryId)
            .pipe(
                map(resp => {
                    if (resp.Models !== undefined) {
                        return DeviceActions.getNewDeviceModelsSuccess({ deviceModel: resp.Models });
                    }
                    return DeviceActions.getNewDeviceModelsFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getNewDeviceModelsFailed({ errorMessage: error.message })))
            )
        )
    ));

    getGateStates$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getGateStates),
        concatMap(action => of(action).pipe(
            withLatestFrom(this.store.select('user', 'currentLang')),
        )),
        switchMap(([action, lang]) => {
            let language = lang.code;
            if (action.language) {
                language = action.language;
            }
            return this.deviceService.getGateStates(language)
                .pipe(
                    map(resp => {
                        if (resp.Success) {
                            return DeviceActions.getGateStatesSuccess({ gateStates: resp.Data.States });
                        } else {
                            return DeviceActions.getGateStatesFailed({ errorMessage: resp.ErrorMessage });
                        }

                    }),
                    catchError(error => {
                        console.error(error);
                        return of(DeviceActions.getGateStatesFailed({ errorMessage: error.message }));
                    })
                );
        }
        )
    ));

    getGateErrors$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getGateErrors),
        concatMap(action => of(action).pipe(
            withLatestFrom(this.store.select('user', 'currentLang')),
        )),
        switchMap(([action, lang]) => {
            let language = lang.code;
            if (action.language) {
                language = action.language;
            }
            return this.deviceService.getGateErrors(language)
                .pipe(
                    map(resp => {
                        if (resp.Success) {
                            return DeviceActions.getGateErrorsSuccess({ gateErrors: resp.Data.Errors });
                        } else {
                            return DeviceActions.getGateErrorsFailed({ errorMessage: resp.ErrorMessage });
                        }

                    }),
                    catchError(error => {
                        console.error(error);
                        return of(DeviceActions.getGateErrorsFailed({ errorMessage: error.message }));
                    })
                );
        }
        )
    ));

    getConfigStates$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getDeviceModelSuccess),
        concatMap(action => of(action).pipe(
            withLatestFrom(this.store.select('user', 'currentLang')),
        )),
        switchMap(([action, lang]) => this.deviceService.getConfigStates(action.deviceModel.Id, lang.code)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.getConfigStatesSuccess({ configStates: resp.Data.ConfigurationStates });
                    } else {
                        return DeviceActions.getConfigStatesFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(DeviceActions.getConfigStatesFailed({ errorMessage: error.message }));
                })
            )
        )
    ));

    initDevice$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.initDevice),
        concatMap(action => of(action).pipe(
            withLatestFrom(this.store.select('device', 'currentDevice')),
        )),
        switchMap(([action, device]) => {
            let d = device;
            if (action.device) {
                d = action.device;
            }
            return this.deviceService.initDevice(d, action.terminals)
                .pipe(
                    map(resp => {
                        if (resp.Success) {
                            return DeviceActions.initDeviceSuccess({ id: d.Id, message: this.translate.instant('INIT_DEVICE_SUCCESS') });
                        } else {
                            return DeviceActions.initDeviceFailed({ errorMessage: resp.ErrorMessage });
                        }
                    }),
                    catchError(error => of(DeviceActions.initDeviceFailed({ errorMessage: error.message })))
                );
        })
    ));

    getSignalLevel$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getSignalLevel),
        concatMap(action => of(action).pipe(
            withLatestFrom(this.store.select('device', 'currentDevice')),
        )),
        switchMap(([action, currentDevice]) => {
            let deviceId = currentDevice.Id;
            let categoryId = currentDevice.CategoryId;
            if (action.deviceId) {
                deviceId = action.deviceId;
                categoryId = action.categoryId;
            }
            return this.deviceService.readSignalLevel(deviceId, categoryId)
                .pipe(
                    map(resp => {
                        if (resp.Success) {
                            return DeviceActions.getSignalLevelSuccess({ signalData: resp.Data });
                        } else {
                            return DeviceActions.getSignalLevelFailed({ errorMessage: resp.ErrorMessage });
                        }

                    }),
                    catchError(error => {
                        console.error(error);
                        return of(DeviceActions.getSignalLevelFailed({ errorMessage: error.message }));
                    })
                );
        })
    ));

    getCommands$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getCommands, DeviceActions.getCurrentDeviceSuccess),
        concatMap(action => of(action).pipe(
            withLatestFrom(
                this.store.select('user', 'currentLang'),
                this.store.select('device', 'currentDevice'),
            ),
        )),
        filter(
            // let pass only devices supporting commands
            ([action, lang, device]) => {
                const dev = action.device ? action.device : device;
                return supportCommands(dev.ModelId, dev.CategoryId);
            }
        ),
        switchMap(([action, lang, device]) => {
            const dev = action.device ? action.device : device;
            const deviceId = dev.Id;
            const modelId = dev.ModelId;
            return this.deviceService.getDeviceCommands(deviceId, modelId, lang.code)
                .pipe(
                    map(resp => {
                        if (resp.Success) {
                            return DeviceActions.getCommandsSuccess({ commands: resp.Data.Commands, connectedAutomationsCommand: resp.Data.ConnectedAutomations });
                        } else {
                            return DeviceActions.getCommandsFailed({ errorMessage: resp.ErrorMessage });
                        }

                    }),
                    catchError(error => of(DeviceActions.getCommandsFailed({ errorMessage: error.message })))
                );
        })
    ));

    editCommands$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editCommands),
        concatMap(action => of(action).pipe(
            withLatestFrom(
                this.store.select('user', 'currentLang'),
                this.store.select('device', 'currentDevice'),
            ),
        )),
        switchMap(([action, lang, device]) => {
            const dev = action.device ? action.device : device;
            const deviceId = dev.Id;
            const payload = action.commands.map(cmd => ({ Id: cmd.Id, Description: cmd.Description }));
            return this.deviceService.editDeviceCommands(deviceId, payload)
                .pipe(
                    map(resp => {
                        if (resp.Success) {
                            return DeviceActions.editCommandsSuccess({
                                commands: action.commands,
                                message: this.translate.instant('COMMANDS_SUCCESSFULLY_EDITED'),
                            });
                        } else {
                            return DeviceActions.editCommandsFailed({ errorMessage: resp.ErrorMessage });
                        }

                    }),
                    catchError(error => of(DeviceActions.editCommandsFailed({ errorMessage: error.message })))
                );
        })
    ));


    executeCommand$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.executeCommand),
        concatMap(action => of(action).pipe(
            withLatestFrom(this.store.select('device', 'currentDevice')),
        )),
        switchMap(([action, currentDevice]) => {
            let deviceId = currentDevice.Id;
            let modelId = currentDevice.ModelId;
            if (action.deviceId && action.modelId) {
                deviceId = action.deviceId;
                modelId = action.modelId;
            }
            return this.deviceService.executeCommand(deviceId, modelId, action.commandId)
                .pipe(
                    map(resp => {
                        if (resp.Success) {
                            return DeviceActions.executeCommandSuccess({ message: this.translate.instant('EXECUTE_COMMAND_SUCCESS') });
                        } else {
                            if (resp.ErrorMessage === '') {
                                return DeviceActions.executeCommandFailed({ errorMessage: this.translate.instant('EXECUTE_COMMAND_FAILED') });
                            }
                            return DeviceActions.executeCommandFailed({ errorMessage: resp.ErrorMessage });
                        }

                    }),
                    catchError(error => of(DeviceActions.executeCommandFailed({ errorMessage: error.message })))
                );
        }
        )
    ));

    getFeatures$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getCurrentDeviceSuccess),
        filter((action) => action.device.CategoryId === EDeviceCategory.Automation),
        switchMap((action) => this.deviceService.getAutomationFeatures(action.device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.getFeaturesSuccess({ features: resp.Data.Features });
                    } else {
                        return DeviceActions.getFeaturesFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.getFeaturesFailed({ errorMessage: error.message })))
            )
        )
    ));

    getDeviceInfo$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getCurrentDeviceSuccess, DeviceActions.getDeviceInfo),
        switchMap((action) => this.deviceService.getDeviceInfo(action.device.Id, action.device.ModelId, action.device.CategoryId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.getDeviceInfoSuccess({ deviceInfo: resp.Data });
                    } else {
                        return DeviceActions.getDeviceInfoFailed({ errorMessage: resp.ErrorMessage });
                    }

                }),
                catchError(error => of(DeviceActions.getDeviceInfoFailed({ errorMessage: error.message })))
            )
        )
    ));

    getDeviceModels$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getDeviceModels),
        concatMap(action => of(action).pipe(
            withLatestFrom(this.store.select('user', 'currentLang')),
        )),
        switchMap(([action, lang]) => {
            let language = lang.code;
            if (action.language) {
                language = action.language;
            }
            return this.deviceService.getDeviceModels(language, action.pagination, action.categoryname, action.categoryid)
                .pipe(
                    map(resp => {
                        if (resp.Success) {
                            return DeviceActions.getDeviceModelsSuccess({ deviceModels: resp.Data.Items });
                        } else {
                            return DeviceActions.getDeviceModelsFailed({ errorMessage: resp.ErrorMessage });
                        }
                    }),
                    catchError(error => of(DeviceActions.getDeviceModelsFailed({ errorMessage: error.message })))
                );
        }
        )
    ));

    getDeviceModelSettings$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getDeviceModelSettings),
        withLatestFrom(
            this.store.select('device', 'currentDeviceModel'),
            this.store.select('user', 'currentLang'),
        ),
        switchMap(([action, model, lang]) => {
            let language = lang.code;
            if (action.language) {
                language = action.language;
            }
            let modelId = model ? model.Id : null;
            if (action.modelId) {
                modelId = action.modelId;
            }
            return this.deviceService.getModelSettings(modelId, language)
                .pipe(
                    map(resp => {
                        if (resp.Success) {
                            return DeviceActions.getDeviceModelSettingsSuccess({ SettingsCategories: resp.Data.SettingsCategories });
                        } else {
                            return DeviceActions.getDeviceModelSettingsFailed({ errorMessage: resp.ErrorMessage });
                        }
                    }),
                    catchError(error => of(DeviceActions.getDeviceModelSettingsFailed({ errorMessage: error.message })))
                );
        }
        )
    ));

    getDeviceSettings$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getDeviceSettings),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
            this.store.select('user', 'currentLang'),
        ),
        switchMap(([action, device, lang]) => this.deviceService.getDeviceSettings(device.Id, lang.code)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.getDeviceSettingsSuccess({ SettingsCategories: resp.Data.SettingsCategories });
                    } else {
                        return DeviceActions.getDeviceSettingsFailed({ errorMessage: resp.ErrorMessage });
                    }

                }),
                catchError(error => of(DeviceActions.getDeviceSettingsFailed({ errorMessage: error.message })))
            )
        )
    ));

    saveDeviceSettings$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.saveDeviceSettings),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        switchMap(([action, device]) => this.deviceService.saveDeviceSettings(device.Id, action.settings)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.saveDeviceSettingsSuccess();
                    } else {
                        return DeviceActions.saveDeviceSettingsFailed({ errorMessage: resp.ErrorMessage });
                    }

                }),
                catchError(error => of(DeviceActions.saveDeviceSettingsFailed({ errorMessage: error.message })))
            )
        )
    ));

    saveDeviceSettingsSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.saveDeviceSettingsSuccess),
        withLatestFrom(this.store.select(selectCurrentDeviceModel)),
        tap(([action, deviceModel]) => {
            setTimeout(() => {
                const productBoardName = deviceModel && deviceModel.ProductBoardName ? deviceModel.ProductBoardName : '';
                const pageTitle = (productBoardName === 'RBEPLUS') ? 'RBEPLUS | Settings' : 'Device | Settings';
                this.store.dispatch(AnalyticsActions.eventDevice({ eventName: 'save_device_settings_completed', params: { page_title: pageTitle } }));
            }, 2000);
        }),
    ), { dispatch: false });

    getBoardUsers$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getBoardusers,
            DeviceActions.deleteBoardUserSuccess,
            DeviceActions.deleteAllBoardUsersSuccess,
            DeviceActions.addBoardUserSuccess,
            DeviceActions.acquireBoardUserSuccess,
            DeviceActions.deleteBoardUserSelectedSuccess,
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
            this.store.select('device', 'boardUsersPaginationParams')
        ),
        switchMap(([action, device, paging]) => {
            let p = paging;
            if (action.paging) {
                p = action.paging;
            }
            return this.boardUsersService.getBoardUsers(device.Id, false, p)
                .pipe(
                    map((resp) => {
                        if (resp.Success) {
                            return DeviceActions.getBoardusersSuccess({ paging: resp.Data.Paging, boardUsers: resp.Data.Items });
                        } else {
                            return DeviceActions.getBoardusersFailed({ errorMessage: resp.ErrorMessage });
                        }
                    }),
                    catchError(error => of(DeviceActions.getBoardusersFailed({ errorMessage: error.message })))
                );
        }
        )
    ));

    getBoardUsersInfo$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getBoardUsersInfo),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
            this.store.select('user', 'currentLang'),
        ),
        switchMap(([action, device, lang]) => {
            let language = lang.code;
            if (action.language) {
                language = action.language;
            }
            return this.boardUsersService.getBoardUsersInfo(device.Id, language).pipe(
                map((resp) => {
                    if (resp.Success) {
                        if (needBUResync(resp.Data)) {
                            return DeviceActions.getBoardusersInfoSuccess({
                                boardUsersInfo: resp.Data,
                                needResync: true,
                                message: this.translate.instant('BOARD_USERS_SYNC_USERS_DEVICES')
                            });
                        }
                        return DeviceActions.getBoardusersInfoSuccess({ boardUsersInfo: resp.Data, needResync: false, message: '' });
                    } else {
                        return DeviceActions.getBoardusersInfoFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.getBoardusersInfoFailed({ errorMessage: error.message })))
            );
        })
    ));

    addBoardUser$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addBoardUser),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        switchMap(([action, device]) => this.boardUsersService.addBoardUser(device.Id, action.boardUser)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.addBoardUserSuccess({ message: this.translate.instant('INSERT_SUCCESS') });
                    } else {
                        if (resp.ErrorMessage === 'GROUP_NOT_SAVED_DEVICE') {
                            return DeviceActions.addBoardUserFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                        }
                        return DeviceActions.addBoardUserFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.addBoardUserFailed({ errorMessage: error.message })))
            )
        )
    ));

    acquireBoardUser$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.acquireBoardUser),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.boardUsersService.acquireBoardUser(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.acquireBoardUserSuccess({
                            boardUser: resp.Data,
                            message: this.translate.instant('ACQUIRE_CODE_SUCCESS'),
                        });
                    } else {
                        if (resp.ErrorCode === 130) { // TRANSLATE ERROR: timeout acquire of new user
                            return DeviceActions.acquireBoardUserFailed({ errorMessage: this.translate.instant('NEW_TIMEOUT_RADIO_CONTROL_ACQUISITION') });
                        } else {
                            return DeviceActions.acquireBoardUserFailed({ errorMessage: resp.ErrorMessage });
                        }
                    }
                }),
                catchError(error => of(DeviceActions.acquireBoardUserFailed({ errorMessage: error.message })))
            )
        )
    ));

    syncBoardUsers$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.syncBoardUsers),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.boardUsersService.syncBoardUsers(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.syncBoardUsersSuccess();
                    } else {
                        if (resp.ErrorCode === 55) { // TRANSLATE ERROR: timeout acquire of new user
                            return DeviceActions.syncBoardUsersFailed({ errorMessage: this.translate.instant('SYNC_ERROR_RUNNING_TASK') });
                        } else {
                            return DeviceActions.syncBoardUsersFailed({ errorMessage: resp.ErrorMessage });
                        }
                    }
                }),
                catchError(error => of(DeviceActions.syncBoardUsersFailed({ errorMessage: error.message })))
            )
        )
    ));

    syncBoardUsersSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.syncBoardUsersSuccess),
        withLatestFrom(this.store.select(selectCurrentDeviceModel)),
        tap(([action, deviceModel]) => {
            setTimeout(() => {
                const productBoardName = deviceModel && deviceModel.ProductBoardName ? deviceModel.ProductBoardName : '';
                const pageTitle = (productBoardName === 'RBEPLUS') ? 'RBEPLUS | BoardUsers' : 'BoardUsers';
                this.store.dispatch(AnalyticsActions.eventDevice({ eventName: 'sync_boardusers_submited', params: { page_title: pageTitle } }));
            }, 2000);

        }),
    ), { dispatch: false });

    editBoardUser$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editBoardUser),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        switchMap(([action, device]) => this.boardUsersService.editBoardUser(device.Id, action.boardUser, action.isCodeGroup)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editBoardUserSuccess({ message: this.translate.instant('EDIT_SUCCESS') });
                    } else {
                        if (resp.ErrorMessage === 'GROUP_NOT_SAVED_DEVICE') {
                            return DeviceActions.editBoardUserFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                        }
                        return DeviceActions.editBoardUserFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.editBoardUserFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteBoardUser$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteBoardUser),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        switchMap(([action, device]) => this.boardUsersService.deleteBoardUser(device.Id, action.position, action.code)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteBoardUserSuccess({ message: this.translate.instant('DELETE_SUCCESS') });
                    } else {
                        return DeviceActions.deleteBoardUserFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteBoardUserFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteBoardUserSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteBoardUserSuccess),
        withLatestFrom(this.store.select(selectCurrentDeviceModel)),
        tap(([action, deviceModel]) => {
            setTimeout(() => {
                const productBoardName = deviceModel && deviceModel.ProductBoardName ? deviceModel.ProductBoardName : '';
                const pageTitle = (productBoardName === 'RBEPLUS') ? 'RBEPLUS | BoardUsers' : 'BoardUsers';
                this.store.dispatch(AnalyticsActions.eventDevice({ eventName: 'delete_boarduser_completed', params: { page_title: pageTitle } }));
            }, 2000);
        }),
    ), { dispatch: false });

    addBoardUsersList$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addBoardUsers),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.boardUsersService.addBoardUsers(device.Id, action.boardUsers)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.addBoardUsersSuccess({ message: this.translate.instant('IMPORT_STARTED') });
                    } else {
                        return DeviceActions.addBoardUsersFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.addBoardUsersFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteAllBoardUsers$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteAllBoardUsers),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        switchMap(([action, device]) => this.boardUsersService.deleteAllBoardUsers(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteAllBoardUsersSuccess({ message: this.translate.instant('AC_RBE_RESET_SUCCESS') });
                    } else {
                        return DeviceActions.deleteAllBoardUsersFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteAllBoardUsersFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteAllBoardUsersSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteAllBoardUsersSuccess),
        withLatestFrom(this.store.select(selectCurrentDeviceModel)),
        tap(([action, deviceModel]) => {
            setTimeout(() => {
                const productBoardName = deviceModel && deviceModel.ProductBoardName ? deviceModel.ProductBoardName : '';
                const pageTitle = (productBoardName === 'RBEPLUS') ? 'RBEPLUS | BoardUsers' : 'BoardUsers';
                this.store.dispatch(AnalyticsActions.eventDevice({ eventName: 'delete_all_boardusers_completed', params: { page_title: pageTitle } }));
            }, 2000);
        }),
    ), { dispatch: false });

    deleteBoardUserSelected$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteBoardUserSelected),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        switchMap(([action, device]) => this.boardUsersService.deleteBoardUserSelected(device.Id, action.boardUsers)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteBoardUserSelectedSuccess({ message: this.translate.instant('DELETE_SUCCESS') });
                    } else {
                        return DeviceActions.deleteBoardUserSelectedFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteBoardUserSelectedFailed({ errorMessage: error.message })))
            )
        )
    ));

    reloadBoardUsersInfoAfterDelete$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteAllBoardUsersSuccess),
        switchMap((device) => [
            DeviceActions.getBoardUsersInfo({}),
        ])
    ));

    getDeviceModelManuals$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getDeviceModelSuccess),
        withLatestFrom(
            this.store.select('user', 'currentLang'),
        ),
        switchMap(([action, lang]) => this.deviceService.getDeviceModelManuals(action.deviceModel.Id, lang.code)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.getDeviceModelManualsSuccess({ manuals: resp.Data.Manuals });
                    } else {
                        return DeviceActions.getDeviceModelManualsFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.getDeviceModelManualsFailed({ errorMessage: error.message })))
            )
        )
    ));

    getErrors$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getErrorCodes),
        withLatestFrom(this.store.select('user', 'currentLang')),
        switchMap(([action, lang]) => this.deviceService.getErrorCodes(lang.code)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.getErrorCodesSuccess({ errorCodes: resp.Data.ErrorCodes });
                    } else {
                        return DeviceActions.getErrorCodesFailed({ errorMessage: resp.ErrorMessage });
                    }

                }),
                catchError(error => of(DeviceActions.getErrorCodesFailed({ errorMessage: error.message })))
            )
        )
    ));

    updateMultiIO$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editMultiIO),
        switchMap((action) => this.deviceService.editMultiIO(action.device, action.ioPayload)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editMultiIOSuccess({
                            device: action.device,
                            message: this.translate.instant('MULTI_IO_EDITED')
                        });
                    } else {
                        return DeviceActions.editMultiIOFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.editMultiIOFailed({ errorMessage: error.message })))
            )
        )
    ));

    updateMultiIOSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editMultiIOSuccess),
        map(action => action.device),
        switchMap((device) => [
            DeviceActions.getDeviceInfo({ device }),
            DeviceActions.getCommands({ device }),
        ])
    ));

    getRemoteClients$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getRemoteClients),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
            this.store.select('user', 'currentUser'),
        ),
        switchMap(([action, device, user]) => {
            const deviceId = action.deviceId ? action.deviceId : device.Id;
            const userId = action.userId ? action.userId : user.Id;
            return this.deviceService.getRemoteClients(deviceId, userId).pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.getRemoteClientsSuccess({ remoteClients: resp.Data });
                    } else {
                        return DeviceActions.getRemoteClientsFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.getRemoteClientsFailed({ errorMessage: error.message })))
            );
        })
    ));

    getFinalUserRemoteClients$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getFinalUserRemoteClients),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => {
            const deviceId = action.deviceId ? action.deviceId : device.Id;
            return this.deviceService.getRemoteClients(deviceId, action.userId)
                .pipe(
                    map(resp => {
                        if (resp.Success) {
                            return DeviceActions.getFinalUserRemoteClientsSuccess({ remoteClients: resp.Data, userId: action.userId });
                        } else {
                            return DeviceActions.getFinalUserRemoteClientsFailed({ errorMessage: resp.ErrorMessage });
                        }
                    }),
                    catchError(error => of(DeviceActions.getFinalUserRemoteClientsFailed({ errorMessage: error.message })))
                );
        })
    ));

    deleteRemoteClient$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteRemoteClient),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        switchMap(([action, device]) => this.deviceService.deleteRemoteClient(device.Id, action.slotId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteRemoteClientSuccess({ slotId: action.slotId, message: this.translate.instant('DELETE_AUTOMATION_CLIENT_SUCCESS_MESSAGE') });
                    } else {
                        return DeviceActions.deleteRemoteClientFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteRemoteClientFailed({ errorMessage: error.message })))
            )
        )
    ));

    reloadRemoteClients$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteRemoteClientSuccess),
        mergeMap(() => [DeviceActions.getRemoteClients({})])
    ));

    toggleRemoteClientLock$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.toggleRemoteClientLock),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        switchMap(([action, device]) => this.deviceService.toggleRemoteClientLock(device.Id, action.slotId, action.locked)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.toggleRemoteClientLockSuccess({ slotId: action.slotId, locked: action.locked, message: this.translate.instant('LOCK_UNLOCK_SUCCESS_MESSAGE') });
                    } else {
                        return DeviceActions.toggleRemoteClientLockFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.toggleRemoteClientLockFailed({ errorMessage: error.message })))
            )
        )
    ));

    getFinalUsers$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getFinalUsers, DeviceActions.shareDeviceSuccess),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        switchMap(([action, device]) => this.userService.getFinalUsers(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.getFinalUsersSuccess({ finalUsers: resp.Data });
                    } else {
                        return DeviceActions.getFinalUsersFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.getFinalUsersFailed({ errorMessage: error.message })))
            )
        )
    ));

    getProfiles$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getProfiles),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        switchMap(([action, device]) => this.profilesService.getDeviceProfiles(device.Id, device.CategoryId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.getProfilesSuccess({ profiles: resp.Data.Items });
                    } else {
                        return DeviceActions.getProfilesFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.getProfilesFailed({ errorMessage: error.message })))
            )
        )
    ));

    getUserProfiles$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getUserProfiles),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        switchMap(([action, device]) => this.profilesService.getDeviceProfiles(device.Id, device.CategoryId, action.userId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.getUserProfilesSuccess({ userId: action.userId, profiles: resp.Data.Items });
                    } else {
                        return DeviceActions.getUserProfilesFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.getUserProfilesFailed({ errorMessage: error.message })))
            )
        )
    ));

    updateUserProfile$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.updateUserProfile),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        switchMap(([action, device]) => this.profilesService.updateDeviceProfile(
            device.Id, device.CategoryId, action.id, action.checked, action.userId
        ).pipe(
            map(resp => {
                if (resp.Success) {
                    return DeviceActions.updateUserProfileSuccess({
                        id: action.id, checked: action.checked, userId: action.userId, message: this.translate.instant('EDIT_SUCCESS'),
                    });
                } else {
                    return DeviceActions.updateUserProfileFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => of(DeviceActions.updateUserProfileFailed({ errorMessage: error.message })))
        ))
    ));

    shareDevice$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.shareDevice),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        switchMap(([action, device]) => this.deviceService.shareDevice(
            action.email, action.firstName, action.lastName, device.Id, action.remotesMax
        ).pipe(
            map(resp => {
                if (resp.Success) {
                    return DeviceActions.shareDeviceSuccess({ message: this.translate.instant('SHARE_DEVICE_SUCCESS') });
                } else {
                    if (resp.ErrorMessage.indexOf('user role not allowed')) {
                        return DeviceActions.shareDeviceFailed({ errorMessage: this.translate.instant('SHARE_DEVICE_FAILED_ONLY_USER_ONE') });
                    }
                    return DeviceActions.shareDeviceFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                }
            }),
            catchError(error => of(DeviceActions.shareDeviceFailed({ errorMessage: error.message })))
        ))
    ));

    removeShareDevice$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.removeShareDevice),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        switchMap(([action, device]) => this.deviceService.removeShareDevice(device.Id, action.userId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.removeShareDeviceSuccess({ userId: action.userId });
                    } else {
                        return DeviceActions.removeShareDeviceFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.removeShareDeviceFailed({ errorMessage: error.message })))
            )
        )
    ));

    changeMaxSlots$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.changeMaxSlots),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        switchMap(([action, device]) => this.deviceService.patchDeviceShare(device.Id, action.userId, action.maxRemotes)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.changeMaxSlotsSuccess({ message: this.translate.instant("MAX_REMOTES_EDIT_SUCCESS_MESSAGE") });
                    } else {
                        return DeviceActions.changeMaxSlotsFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.changeMaxSlotsFailed({ errorMessage: error.message })))
            )
        )
    ));
    getSafetyInputs$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getSafetyInputs),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        switchMap(([action, device]) => this.deviceService.getSafetyInputs(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.getSafetyInputsSuccess({ safetyInputs: resp.Data.SafetyInputsCategories });
                    }
                    return DeviceActions.getSafetyInputsFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getSafetyInputsFailed({ errorMessage: error.message })))
            )
        )
    ));

    confirmShare$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.confirmShareDevice),
        switchMap((action) => this.deviceService.confirmShareDevice(action.invitationData).pipe(
            map(resp => {
                if (resp.Success) {
                    return DeviceActions.confirmShareDeviceSuccess({ message: this.translate.instant('CONFIRM_SHARE_DEVICE_SUCCESS') });
                } else {
                    return DeviceActions.confirmShareDeviceFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => of(DeviceActions.confirmShareDeviceFailed({ errorMessage: error.message })))
        ))
    ));

    reloadSites$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.confirmShareDeviceSuccess),
        mergeMap((action) => [SiteActions.getSites({}), SiteActions.getSitesOnlineDevices({})])
    ));

    getMtmaActions$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaActions),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaActions(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const actions = resp.Data;
                        return DeviceActions.getMtmaActionsSuccess({ actions });
                    }
                    return DeviceActions.getMtmaActionsFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaActionsFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaModules$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaModules, DeviceActions.addMtmaModuleSuccess,
            DeviceActions.editMtmaModuleSuccess, DeviceActions.deleteMtmaModuleSuccess,
            DeviceActions.addMtmaModuleFailed,
            DeviceActions.editMtmaModuleFailed, DeviceActions.deleteMtmaModuleFailed),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaModules(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const modules = resp.Data;
                        return DeviceActions.getMtmaModulesSuccess({ modules });
                    }
                    return DeviceActions.getMtmaModulesFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaModulesFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaModule$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getMtmaModule
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaModule(device.Id, action.moduleId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const module = resp.Data;
                        return DeviceActions.getMtmaModuleSuccess({ module });
                    }
                    return DeviceActions.getMtmaModuleFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaModuleFailed({ errorMessage: error.message })))
            )
        )
    ));

    addMtmaModule$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addMtmaModule),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.addMtmaModule(device.Id, action.module)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.addMtmaModuleSuccess({ message: this.translate.instant('ADD_MTMA_MODULE_SUCCESS') });
                    } else {
                        return DeviceActions.addMtmaModuleFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.addMtmaModuleFailed({ errorMessage: error.message })))
            )
        )
    ));

    editMtmaModule$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editMtmaModule),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editMtmaModule(device.Id, action.module)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editMtmaModuleSuccess({ message: this.translate.instant('EDIT_MTMA_MODULE_SUCCESS') });
                    } else {
                        return DeviceActions.editMtmaModuleFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.editMtmaModuleFailed({ errorMessage: error.message })))
            )
        )
    ));

    editParamMtmaModule$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editParamMtmaModule),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editParamMtmaModule(device.Id, action.module)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editParamMtmaModuleSuccess({ message: this.translate.instant('EDIT_MTMA_MODULE_SUCCESS') });
                    } else {
                        return DeviceActions.editParamMtmaModuleFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.editParamMtmaModuleFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteMtmaModule$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteMtmaModule),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.deleteMtmaModule(device.Id, action.moduleId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteMtmaModuleSuccess({ message: this.translate.instant('DELETE_MTMA_MODULE_SUCCESS') });
                    } else {
                        return DeviceActions.deleteMtmaModuleFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteMtmaModuleFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaContacts$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaContacts),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaContacts(device.Id, action.filter,
            action.sort, action.order, action.size, action.page)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaContactsSuccess({
                            contacts: respData.Contacts,
                            paging: respData.Paging, maxData: respData.Counters
                        });
                    }
                    return DeviceActions.getMtmaContactsFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaContactsFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaContactsAll$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaContactsAll),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaContacts(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaContactsAllSuccess({ contacts: respData.Contacts });
                    }
                    return DeviceActions.getMtmaContactsAllFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaContactsAllFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaContact$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getMtmaContact
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaContact(device.Id, action.contact.ObjId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const contact = resp.Data;
                        return DeviceActions.getMtmaContactSuccess({ contactRequest: action.contact, contactResponse: contact });
                    }
                    return DeviceActions.getMtmaContactFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaContactFailed({ errorMessage: error.message })))
            )
        )
    ));

    addMtmaContact$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addMtmaContact),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.addMtmaContact(device.Id, action.contact)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.addMtmaContactSuccess({ message: this.translate.instant('ADD_MTMA_CONTACT_SUCCESS') });
                    } else {
                        return DeviceActions.addMtmaContactFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(DeviceActions.addMtmaContactFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                })
            )
        )
    ));

    editMtmaContact$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editMtmaContact),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editMtmaContact(device.Id, action.contact)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editMtmaContactSuccess({ message: this.translate.instant('EDIT_MTMA_CONTACT_SUCCESS') });
                    } else {
                        return DeviceActions.editMtmaContactFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.editMtmaContactFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteMtmaContact$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteMtmaContact),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.deleteMtmaContact(device.Id, action.contactId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteMtmaContactSuccess({ message: this.translate.instant('DELETE_MTMA_CONTACT_SUCCESS') });
                    } else {
                        return DeviceActions.deleteMtmaContactFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteMtmaContactFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaUnits$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaUnits),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaUnits(device.Id, action.filter,
            action.sort, action.order, action.size, action.page)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaUnitsSuccess({
                            units: respData.Units,
                            paging: respData.Paging, maxData: respData.Counters
                        });
                    }
                    return DeviceActions.getMtmaUnitsFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaUnitsFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaUnit$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getMtmaUnit
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaUnit(device.Id, action.unit.ObjId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const unit = resp.Data;
                        return DeviceActions.getMtmaUnitSuccess({ unitRequest: action.unit, unitResponse: unit });
                    }
                    return DeviceActions.getMtmaUnitFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaUnitFailed({ errorMessage: error.message })))
            )
        )
    ));

    addMtmaUnit$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addMtmaUnit),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.addMtmaUnit(device.Id, action.unit)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.addMtmaUnitSuccess({ message: this.translate.instant('ADD_MTMA_UNIT_SUCCESS') });
                    } else {
                        return DeviceActions.addMtmaUnitFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.addMtmaUnitFailed({ errorMessage: error.message })))
            )
        )
    ));

    editMtmaUnit$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editMtmaUnit),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editMtmaUnit(device.Id, action.unit)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editMtmaUnitSuccess({ message: this.translate.instant('EDIT_MTMA_UNIT_SUCCESS') });
                    } else {
                        return DeviceActions.editMtmaUnitFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.editMtmaUnitFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteMtmaUnit$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteMtmaUnit),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.deleteMtmaUnit(device.Id, action.unitId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteMtmaUnitSuccess({ message: this.translate.instant('DELETE_MTMA_UNIT_SUCCESS') });
                    } else {
                        return DeviceActions.deleteMtmaUnitFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteMtmaUnitFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaDtmfs$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaDtmfs),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaDtmfs(device.Id, action.filter,
            action.sort, action.order, action.size, action.page)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaDtmfsSuccess({
                            dtmfs: respData.DtmfTones,
                            paging: respData.Paging, maxData: respData.Counters
                        });
                    }
                    return DeviceActions.getMtmaDtmfsFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaDtmfsFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaDtmf$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getMtmaDtmf
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaDtmf(device.Id, action.dtmfId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const dtmf = resp.Data;
                        return DeviceActions.getMtmaDtmfSuccess({ dtmf });
                    }
                    return DeviceActions.getMtmaDtmfFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaDtmfFailed({ errorMessage: error.message })))
            )
        )
    ));

    addMtmaDtmf$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addMtmaDtmf),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.addMtmaDtmf(device.Id, action.dtmf)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.addMtmaDtmfSuccess({ message: this.translate.instant('ADD_MTMA_DTMF_SUCCESS') });
                    } else {
                        return DeviceActions.addMtmaDtmfFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined && (error.error.ErrorMessage === 'MTMA_DTMF_CODE_SAME' ||
                        error.error.ErrorMessage === 'MTMA_DTMF_ACTION_SAME')) {
                        return of(DeviceActions.addMtmaDtmfFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.addMtmaDtmfFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    editMtmaDtmf$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editMtmaDtmf),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editMtmaDtmf(device.Id, action.dtmf)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editMtmaDtmfSuccess({ message: this.translate.instant('EDIT_MTMA_DTMF_SUCCESS') });
                    } else {
                        return DeviceActions.editMtmaDtmfFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined && (error.error.ErrorMessage === 'MTMA_DTMF_CODE_SAME' ||
                        error.error.ErrorMessage === 'MTMA_DTMF_ACTION_SAME')) {
                        return of(DeviceActions.addMtmaDtmfFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.addMtmaDtmfFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    deleteMtmaDtmf$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteMtmaDtmf),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.deleteMtmaDtmf(device.Id, action.dtmfId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteMtmaDtmfSuccess({ message: this.translate.instant('DELETE_MTMA_DTMF_SUCCESS') });
                    } else {
                        return DeviceActions.deleteMtmaDtmfFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteMtmaDtmfFailed({ errorMessage: error.message })))
            )
        )
    ));


    getMtmaAutomationCommands$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaAutomationCommands),
        concatMap(action => of(action).pipe(
            withLatestFrom(
                this.store.select('user', 'currentLang'),
            ),
        )),
        switchMap(([action, lang]) => {
            const deviceId = action.deviceId;
            const modelId = -1;
            return this.deviceService.getDeviceCommands(deviceId, modelId, lang.code)
                .pipe(
                    map(resp => {
                        if (resp.Success) {
                            return DeviceActions.getMtmaAutomationCommandsSuccess({ commands: resp.Data.Commands, automationId: deviceId });
                        } else {
                            return DeviceActions.getMtmaAutomationCommandsFailed({ errorMessage: resp.ErrorMessage });
                        }

                    }),
                    catchError(error => of(DeviceActions.getMtmaAutomationCommandsFailed({ errorMessage: error.message })))
                );
        })
    ));



    getMtmaTags$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaTags),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaTags(device.Id, action.filter,
            action.sort, action.order, action.size, action.page)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaTagsSuccess({
                            tags: respData.Tags,
                            paging: respData.Paging, maxData: respData.Counters
                        });
                    }
                    return DeviceActions.getMtmaTagsFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaTagsFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaTag$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getMtmaTag
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaTag(device.Id, action.tagId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const tag = resp.Data;
                        return DeviceActions.getMtmaTagSuccess({ tag });
                    }
                    return DeviceActions.getMtmaTagFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaTagFailed({ errorMessage: error.message })))
            )
        )
    ));

    addMtmaTag$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addMtmaTag),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.addMtmaTag(device.Id, action.tag)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.addMtmaTagSuccess({ message: this.translate.instant('ADD_MTMA_TAG_SUCCESS') });
                    } else {
                        return DeviceActions.addMtmaTagFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined && (error.error.ErrorMessage === 'MTMA_TAG_CODE_SAME')) {
                        return of(DeviceActions.getMtmaTagsFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.getMtmaTagsFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    editMtmaTag$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editMtmaTag),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editMtmaTag(device.Id, action.tag)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editMtmaTagSuccess({ message: this.translate.instant('EDIT_MTMA_TAG_SUCCESS') });
                    } else {
                        return DeviceActions.editMtmaTagFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined && (error.error.ErrorMessage === 'MTMA_TAG_CODE_SAME')) {
                        return of(DeviceActions.getMtmaTagsFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.getMtmaTagsFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    deleteMtmaTag$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteMtmaTag),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.deleteMtmaTag(device.Id, action.tagId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteMtmaTagSuccess({ message: this.translate.instant('DELETE_MTMA_TAG_SUCCESS') });
                    } else {
                        return DeviceActions.deleteMtmaTagFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteMtmaTagFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaKeypasdAll$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaKeypadsAll),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaKeypads(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaKeypadsAllSuccess({ keypads: respData.Keypads });
                    }
                    return DeviceActions.getMtmaKeypadsAllFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaKeypadsAllFailed({ errorMessage: error.message })))
            )
        )
    ));


    getMtmaKeypads$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaKeypads),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaKeypads(device.Id, action.filter,
            action.sort, action.order, action.size, action.page)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaKeypadsSuccess({
                            keypads: respData.Keypads,
                            paging: respData.Paging, maxData: respData.Counters, invalid: respData.Invalid
                        });
                    }
                    return DeviceActions.getMtmaKeypadsFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaKeypadsFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaKeypad$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getMtmaKeypad
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaKeypad(device.Id, action.keypadId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const keypad = resp.Data;
                        return DeviceActions.getMtmaKeypadSuccess({ keypad });
                    }
                    return DeviceActions.getMtmaKeypadFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaKeypadFailed({ errorMessage: error.message })))
            )
        )
    ));

    addMtmaKeypad$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addMtmaKeypad),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.addMtmaKeypad(device.Id, action.keypad)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.addMtmaKeypadSuccess({ message: this.translate.instant('ADD_MTMA_KEYPAD_SUCCESS') });
                    } else {
                        return DeviceActions.addMtmaKeypadFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined && (error.error.ErrorMessage === 'MTMA_KEYPAD_CODE_SAME')) {
                        return of(DeviceActions.addMtmaKeypadFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.addMtmaKeypadFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    editMtmaKeypad$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editMtmaKeypad),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editMtmaKeypad(device.Id, action.keypad)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editMtmaKeypadSuccess({ message: this.translate.instant('EDIT_MTMA_KEYPAD_SUCCESS') });
                    } else {
                        return DeviceActions.editMtmaKeypadFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined && (error.error.ErrorMessage === 'MTMA_KEYPAD_CODE_SAME')) {
                        return of(DeviceActions.editMtmaKeypadFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.editMtmaKeypadFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    deleteMtmaKeypad$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteMtmaKeypad),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.deleteMtmaKeypad(device.Id, action.keypadId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteMtmaKeypadSuccess({ message: this.translate.instant('DELETE_MTMA_KEYPAD_SUCCESS') });
                    } else {
                        return DeviceActions.deleteMtmaKeypadFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteMtmaKeypadFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaTagsAll$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaTagsAll),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaTags(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaTagsAllSuccess({ tags: respData.Tags });
                    }
                    return DeviceActions.getMtmaTagsAllFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaTagsAllFailed({ errorMessage: error.message })))
            )
        )
    ));


    getMtmaSettingsModules$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getMtmaSettingsModules
        ),
        switchMap(action => this.deviceService.getMtmaSettingsModules()
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const listModulesSettings = resp.Data;
                        return DeviceActions.getMtmaSettingsModulesSuccess({ listModulesSettings });
                    }
                    return DeviceActions.getMtmaSettingsModulesFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaSettingsModulesFailed({ errorMessage: error.message })))
            )
        )
    ));


    getMtmaChanges$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getMtmaChanges
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaChanges(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const changes = resp.Data;
                        return DeviceActions.getMtmaChangesSuccess({ changes });
                    }
                    return DeviceActions.getMtmaChangesFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaChangesFailed({ errorMessage: error.message })))
            )
        )
    ));


    getMtmaActiveTask$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getMtmaActiveTask
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaActiveTask(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const task = resp.Data;
                        return DeviceActions.getMtmaActiveTaskSuccess({ task });
                    }
                    return DeviceActions.getMtmaActiveTaskFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaActiveTaskFailed({ errorMessage: error.message })))
            )
        )
    ));


    mtmaWriteConfig$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.mtmaWriteConfig
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.mtmaWriteConfig(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const task = resp.Data;
                        return DeviceActions.mtmaWriteConfigSuccess({ task });
                    }
                    return DeviceActions.mtmaWriteConfigFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.mtmaWriteConfigFailed({ errorMessage: error.message })))
            )
        )
    ));


    getMtmaAbort$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getMtmaAbort
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.mtmaAbortConfig(device.Id, action.taskId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const task = resp.Data;
                        return DeviceActions.getMtmaAbortSuccess({ task });
                    }
                    return DeviceActions.getMtmaAbortFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaAbortFailed({ errorMessage: error.message })))
            )
        )
    ));


    // MTMA TIMESLICE
    getMtmaTimeSlices$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaTimeSlices),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaTimeslices(device.Id, action.filter,
            action.sort, action.order, action.size, action.page)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaTimeSlicesSuccess({
                            timeslices: respData.Timeslices,
                            paging: respData.Paging, maxData: respData.Counters
                        });
                    }
                    return DeviceActions.getMtmaTimeSlicesFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaTimeSlicesFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaTimeSlice$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getMtmaTimeSlice
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaTimeslice(device.Id, action.timesliceId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const timeslice = resp.Data;
                        return DeviceActions.getMtmaTimeSliceSuccess({ timeslice });
                    }
                    return DeviceActions.getMtmaTimeSliceFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaTimeSliceFailed({ errorMessage: error.message })))
            )
        )
    ));

    addMtmaTimeSlice$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addMtmaTimeSlice),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.addMtmaTimeslice(device.Id, action.timeslice)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.addMtmaTimeSliceSuccess({ message: this.translate.instant('ADD_MTMA_TIMESLICE_SUCCESS') });
                    } else {
                        return DeviceActions.addMtmaTimeSliceFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        return of(DeviceActions.addMtmaTimeSliceFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.addMtmaTimeSliceFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    editMtmaTimeSlice$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editMtmaTimeSlice),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editMtmaTimeslice(device.Id, action.timeslice)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editMtmaTimeSliceSuccess({ message: this.translate.instant('EDIT_MTMA_TIMESLICE_SUCCESS') });
                    } else {
                        return DeviceActions.editMtmaTimeSliceFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        return of(DeviceActions.editMtmaTimeSliceFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.editMtmaTimeSliceFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    deleteMtmaTimeSlice$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteMtmaTimeSlice),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.deleteMtmaTimeslice(device.Id, action.timesliceId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteMtmaTimeSliceSuccess({ message: this.translate.instant('DELETE_MTMA_TIMESLICE_SUCCESS') });
                    } else {
                        return DeviceActions.deleteMtmaTimeSliceFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteMtmaTimeSliceFailed({ errorMessage: error.message })))
            )
        )
    ));


    getMtmaTimeSlicesAll$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaTimeSlicesAll),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaTimeslices(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaTimeSlicesAllSuccess({ timeslices: respData.Timeslices });
                    }
                    return DeviceActions.getMtmaTimeSlicesAllFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaTimeSlicesAllFailed({ errorMessage: error.message })))
            )
        )
    ));

    // MTMA RULES
    getMtmaRules$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaRules),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaRules(device.Id, action.filter,
            action.sort, action.order, action.size, action.page)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaRulesSuccess({
                            rules: respData.Rules,
                            paging: respData.Paging, maxData: respData.Counters
                        });
                    }
                    return DeviceActions.getMtmaRulesFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaRulesFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaRule$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getMtmaRule
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaRule(device.Id, action.ruleId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const rule = resp.Data;
                        return DeviceActions.getMtmaRuleSuccess({ rule });
                    }
                    return DeviceActions.getMtmaRuleFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaRuleFailed({ errorMessage: error.message })))
            )
        )
    ));

    addMtmaRule$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addMtmaRule),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.addMtmaRule(device.Id, action.rule)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.addMtmaRuleSuccess({ message: this.translate.instant('ADD_MTMA_RULE_SUCCESS') });
                    } else {
                        return DeviceActions.addMtmaRuleFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        return of(DeviceActions.addMtmaRuleFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.addMtmaRuleFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    editMtmaRule$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editMtmaRule),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editMtmaRule(device.Id, action.rule)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editMtmaRuleSuccess({ message: this.translate.instant('EDIT_MTMA_RULE_SUCCESS') });
                    } else {
                        return DeviceActions.editMtmaRuleFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        return of(DeviceActions.addMtmaRuleFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.addMtmaRuleFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    deleteMtmaRule$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteMtmaRule),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.deleteMtmaRule(device.Id, action.ruleId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteMtmaRuleSuccess({ message: this.translate.instant('DELETE_MTMA_RULE_SUCCESS') });
                    } else {
                        return DeviceActions.deleteMtmaRuleFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteMtmaRuleFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) })))
            )
        )
    ));


    getMtmaRulesAll$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaRulesAll),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaRules(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaRulesAllSuccess({ rules: respData.Rules });
                    }
                    return DeviceActions.getMtmaRulesAllFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaRulesAllFailed({ errorMessage: error.message })))
            )
        )
    ));

    // MTMA RULES ACCESS
    getMtmaRulesAccess$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaRulesAccess),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaRuleAccess(device.Id, action.ruleId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaRulesAccessSuccess({ tags: respData.Tags, keypads: respData.Keypads, contacts: respData.Contacts });
                    }
                    return DeviceActions.getMtmaRulesAccessFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaRulesAccessFailed({ errorMessage: error.message })))
            )
        )
    ));


    editMtmaRulesAccess$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editMtmaRulesAccess),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editMtmaRuleAccess(device.Id, action.ruleId, action.ruleAccess)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editMtmaRulesAccessSuccess({ message: this.translate.instant('EDIT_MTMA_RULE_SUCCESS') });
                    } else {
                        return DeviceActions.editMtmaRulesAccessFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        return of(DeviceActions.editMtmaRulesAccessFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.editMtmaRulesAccessFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    cloneMtma$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.cloneMtma),
        withLatestFrom(
            this.store.select('user', 'currentUser')),
        mergeMap(([action, user]) => this.deviceService.cloneMtma(user.Id, action.deviceIdFrom, action.deviceIdTo, action.isImpersMode)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.cloneMtmaSuccess({ message: this.translate.instant('SUB_MTMA_SUCCESS') });
                    } else {
                        if (resp.ErrorMessage === 'error init devices') {
                            return DeviceActions.cloneMtmaFailed({ errorMessage: this.translate.instant('ERROR_INIT_DEVICE_CLONE_MTMA') });
                        }
                        return DeviceActions.cloneMtmaFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        if (error.error.ErrorMessage === 'error init devices') {
                            return of(DeviceActions.cloneMtmaFailed({ errorMessage: this.translate.instant('ERROR_INIT_DEVICE_CLONE_MTMA') }));
                        }
                        return of(DeviceActions.cloneMtmaFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.cloneMtmaFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    getDatetimeDevice$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getDatetimeDevice),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        switchMap(([action, device]) => this.deviceService.getDatetimeDevice(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.getDatetimeDeviceSuccess({ datetimeDevice: resp.Data });
                    } else {
                        return DeviceActions.getDatetimeDeviceFailed({ errorMessage: resp.ErrorMessage });
                    }

                }),
                catchError(error => {
                    console.error(error);
                    return of(DeviceActions.getDatetimeDeviceFailed({ errorMessage: error.message }));
                })
            )
        )
    ));

    //----------------- FIRMWARE MTMA
    getFirmwareUpdate$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getFirmwareUpdate
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.getFirmwareUpdate(device.Id, action.test)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const listUpdateFirmware = resp.Data.Versions;
                        return DeviceActions.getFirmwareUpdateSuccess({ listUpdateFirmware });
                    }
                    return DeviceActions.getFirmwareUpdateFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getFirmwareUpdateFailed({ errorMessage: error.message })))
            )
        )
    ));
    //----------------- FIRMWARE MTMA
    //----------------- FIRMWARE GW

    getGWFirmwareUpdate$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getGWFirmwareUpdate
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.getGWFirmwareUpdate(device.Id, action.test)
            .pipe(
                map(resp => {
                    if (resp.Success && resp.Data !== undefined && resp.Data.Versions) {
                        return DeviceActions.getGWFirmwareUpdateSuccess({ updateFirmware: resp.Data.Versions });
                    } else if (resp.Success && resp.Data !== undefined) {
                        return DeviceActions.getGWFirmwareUpdateSuccess({ updateFirmware: [resp.Data] });
                    } else if (resp.Success && resp.Data === undefined) {
                        return DeviceActions.getGWFirmwareUpdateSuccess({ updateFirmware: [] });
                    }
                    return DeviceActions.getGWFirmwareUpdateFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getGWFirmwareUpdateFailed({ errorMessage: error.message })))
            )
        )
    ));
    //----------------- FIRMWARE GW

    postFirmwareUpdate$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.postFirmwareUpdate),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        mergeMap(([action, device]) => this.deviceService.postFirmwareUpdate(device.Id, action.firmware, action.test)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.postFirmwareUpdateSuccess({ message: this.translate.instant('START_UPDATE_FIRMWARE') });
                    } else {
                        return DeviceActions.postFirmwareUpdateFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined && error.error.ErrorMessage === 'already updating') {
                        return of(DeviceActions.postFirmwareUpdateFailed({ errorMessage: this.translate.instant('UPDATE_VERSION_ALREADY_START') }));
                    } else if (error.error !== undefined) {
                        return of(DeviceActions.postFirmwareUpdateFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.postFirmwareUpdateFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    getRbeAccessLog$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getRbeAccessLog),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getRbeAccessLog(action.deviceId, action.startDate, action.endDate, action.filter,
            action.typeAction, action.result, action.sort, action.order, action.size, action.page, action.output, action.actiondevicename, action.commands)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getRbeAccessLogSuccess({
                            logs: respData.ListAccessEvent,
                            paging: respData.Paging
                        });
                    }
                    return DeviceActions.getRbeAccessLogFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getRbeAccessLogFailed({ errorMessage: error.message })))
            )
        )
    ));

    getCallUnitEventLog$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getCallUnitEventLog),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action]) => this.deviceService.getCallUnitEventLog(action.deviceId, action.startDate, action.endDate, action.unitName,
            action.sort, action.order, action.size, action.page)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getCallUnitEventLogSuccess({
                            logs: respData.ListUnitCallEvent,
                            paging: respData.Paging
                        });
                    }
                    return DeviceActions.getCallUnitEventLogFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getCallUnitEventLogFailed({ errorMessage: error.message })))
            )
        )
    ));

    getRbeAccessLogExport$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getRbeAccessLogExport),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getRbeAccessLog(action.deviceId, action.startDate, action.endDate, action.filter,
            action.typeAction, action.result, action.sort, action.order)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getRbeAccessLogExportSuccess({
                            logs: respData.ListAccessEvent,
                            paging: respData.Paging
                        });
                    }
                    return DeviceActions.getRbeAccessLogExportFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getRbeAccessLogExportFailed({ errorMessage: error.message })))
            )
        )
    ));

    getAccessLog$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getAccessLog),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getAccessLog(action.deviceId, action.startDate, action.endDate, action.filter,
            action.typeAction, action.result, action.sort, action.order, action.size, action.page)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getAccessLogSuccess({
                            logs: respData.ListAccessEvent,
                            paging: respData.Paging
                        });
                    }
                    return DeviceActions.getAccessLogFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getAccessLogFailed({ errorMessage: error.message })))
            )
        )
    ));

    getAccessLogExport$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getAccessLogExport),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getAccessLog(action.deviceId, action.startDate, action.endDate, action.filter,
            action.typeAction, action.result, action.sort, action.order)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getAccessLogExportSuccess({
                            logs: respData.ListAccessEvent,
                            paging: respData.Paging
                        });
                    }
                    return DeviceActions.getAccessLogExportFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getAccessLogExportFailed({ errorMessage: error.message })))
            )
        )
    ));

    getCallUnitLogExport$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getCallUnitLogExport),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action]) => this.deviceService.getCallUnitEventLog(action.deviceId, action.startDate, action.endDate, action.unitName,
            action.sort, action.order)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getCallUnitLogExportSuccess({
                            logs: respData.ListUnitCallEvent
                        });
                    }
                    return DeviceActions.getCallUnitLogExportFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getCallUnitLogExportFailed({ errorMessage: error.message })))
            )
        )
    ));

    getAutomationDiscover$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getAutomationDiscover),
        switchMap((action) => this.deviceService.getAutomationDiscover(action.deviceId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.getAutomationDiscoverSuccess({ message: this.translate.instant('MANUAL_SCAN_AUTOMATION_SUCCESS') });
                    }
                    return DeviceActions.getAutomationDiscoverFailed({ errorMessage: this.translate.instant('MANUAL_SCAN_AUTOMATION_FAILED') });
                }),
                catchError(error => of(DeviceActions.getAutomationDiscoverFailed({ errorMessage: this.translate.instant('MANUAL_SCAN_AUTOMATION_FAILED') })))
            )
        )
    ));

    // DEVICE TIMERS
    getDeviceTimersInfo$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getDeviceTimersInfo),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.getDeviceTimersInfo(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.getDeviceTimersInfoSuccess({ timersInfo: resp.Data });
                    } else {
                        return DeviceActions.getDeviceTimersInfoFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.getDeviceTimersInfoFailed({ errorMessage: error.message })))
            )
        )
    ));


    getDeviceTimers$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getDeviceTimers),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.getDeviceTimers(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success && resp.Data !== undefined) {
                        return DeviceActions.getDeviceTimersSuccess({ timers: resp.Data.Items, meta: resp.Data.Meta });
                    } else {
                        return DeviceActions.getDeviceTimersFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.getDeviceTimersFailed({ errorMessage: error.message })))
            )
        )
    ));


    addDeviceTimer$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addDeviceTimer),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.addDeviceTimer(device.Id, action.timer)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.addDeviceTimerSuccess({ message: this.translate.instant('ADD_DEVICE_TIMER_SUCCESS') });
                    } else {
                        return DeviceActions.addDeviceTimerFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.addDeviceTimerFailed({ errorMessage: error.message })))
            )
        )
    ));

    editDeviceTimer$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editDeviceTimer),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editDeviceTimer(device.Id, action.timer)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editDeviceTimerSuccess({ message: this.translate.instant('EDIT_DEVICE_TIMER_SUCCESS') });
                    } else {
                        return DeviceActions.editDeviceTimerFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                    }
                }),
                catchError(error => of(DeviceActions.editDeviceTimerFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteDeviceTimer$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteDeviceTimer),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.deleteDeviceTimer(device.Id, action.position)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteDeviceTimerSuccess({ message: this.translate.instant('DELETE_DEVICE_TIMER_SUCCESS') });
                    } else {
                        return DeviceActions.deleteDeviceTimerFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                    }
                }),
                catchError(error => of(DeviceActions.deleteDeviceTimerFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteDeviceAllTimer$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteDeviceAllTimer),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.deleteDeviceAllTimer(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteDeviceAllTimerSuccess({ message: this.translate.instant('DELETE_DEVICE_ALL_TIMER_SUCCESS') });
                    } else {
                        return DeviceActions.deleteDeviceAllTimerFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteDeviceAllTimerFailed({ errorMessage: error.message })))
            )
        )
    ));


    // DEVICE SPECIAL DAYS
    getDeviceSpecialDaysInfo$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getDeviceSpecialDaysInfo),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.getDeviceSpecialDaysInfo(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.getDeviceSpecialDaysInfoSuccess({ specialDaysInfo: resp.Data });
                    } else {
                        return DeviceActions.getDeviceSpecialDaysInfoFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.getDeviceSpecialDaysInfoFailed({ errorMessage: error.message })))
            )
        )
    ));


    getDeviceSpecialDays$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getDeviceSpecialDays),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.getDeviceSpecialDays(device.Id, action.from)
            .pipe(
                map(resp => {
                    if (resp.Success && resp.Data !== undefined) {
                        return DeviceActions.getDeviceSpecialDaysSuccess({ specialDays: resp.Data.Items, meta: resp.Data.Meta });
                    } else {
                        return DeviceActions.getDeviceSpecialDaysFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.getDeviceSpecialDaysFailed({ errorMessage: error.message })))
            )
        )
    ));


    addDeviceSpecialDay$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addDeviceSpecialDay),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.addDeviceSpecialDay(device.Id, action.specialDay)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.addDeviceSpecialDaySuccess({ message: this.translate.instant('ADD_DEVICE_SPECIAL_DAY_SUCCESS') });
                    } else {
                        return DeviceActions.addDeviceSpecialDayFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.addDeviceSpecialDayFailed({ errorMessage: error.message })))
            )
        )
    ));

    editDeviceSpecialDay$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editDeviceSpecialDay),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editDeviceSpecialDay(device.Id, action.specialDay)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editDeviceSpecialDaySuccess({ message: this.translate.instant('EDIT_DEVICE_SPECIAL_DAY_SUCCESS') });
                    } else {
                        return DeviceActions.editDeviceSpecialDayFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                    }
                }),
                catchError(error => of(DeviceActions.editDeviceSpecialDayFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteDeviceSpecialDay$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteDeviceSpecialDay),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.deleteDeviceSpecialDay(device.Id, action.position)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteDeviceSpecialDaySuccess({ message: this.translate.instant('DELETE_DEVICE_SPECIAL_DAY_SUCCESS') });
                    } else {
                        return DeviceActions.deleteDeviceSpecialDayFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteDeviceSpecialDayFailed({ errorMessage: error.message })))
            )
        )
    ));


    deleteDeviceAllSpecialDay$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteDeviceAllSpecialDay),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.deleteDeviceAllSpecialDay(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteDeviceAllSpecialDaySuccess({ message: this.translate.instant('DELETE_DEVICE_ALL_SPECIAL_DAY_SUCCESS') });
                    } else {
                        return DeviceActions.deleteDeviceAllSpecialDayFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteDeviceAllSpecialDayFailed({ errorMessage: error.message })))
            )
        )
    ));

    editNameAutomationConnected$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editNameAutomationConnected),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editNameAutomationConnected(device.Id, action.automation)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editNameAutomationConnectedSuccess({ message: this.translate.instant('EDIT_NAME_AUTOMATION_SUCCESS') });
                    } else {
                        return DeviceActions.editNameAutomationConnectedFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                    }
                }),
                catchError(error => of(DeviceActions.editNameAutomationConnectedFailed({ errorMessage: error.message })))
            )
        )
    ));


    // DEVICE STATE
    getStateDeviceByCommandId$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getStateDeviceByCommandId),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.getStateDeviceByCommandId(device.Id, action.commandId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.getStateDeviceByCommandIdSuccess({ deviceState: resp.Data });
                    } else {
                        return DeviceActions.getStateDeviceByCommandIdFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.getStateDeviceByCommandIdFailed({ errorMessage: error.message })))
            )
        )
    ));
    // ------------------------- getNewStateDeviceByCommandId
    getNewStateDeviceByCommandId$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getNewStateDeviceByCommandId),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.getNewStateDeviceByCommandId(device.Id, action.commandId)
            .pipe(
                map(resp => {
                    if (resp !== undefined) {
                        return DeviceActions.getNewStateDeviceByCommandIdSuccess({ deviceState: resp });
                    } else {
                        return DeviceActions.getNewStateDeviceByCommandIdFailed({ errorMessage: this.translate.instant('ERROR_DEVICE_STATE') });
                    }
                }),
                catchError(error => of(DeviceActions.getNewStateDeviceByCommandIdFailed({ errorMessage: error.message })))
            )
        )
    ));
    // ------------------------- getNewStateDeviceByCommandId


    // ------------------------- getNewStateDeviceByCommandId0
    getNewStateDeviceByCommandId0$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getNewStateDeviceByCommandId0),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.getNewStateDeviceByCommandId(device.Id, 0)
            .pipe(
                map(resp => {
                    if (resp !== undefined) {
                        return DeviceActions.getNewStateDeviceByCommandId0Success({ deviceState: resp });
                    } else {
                        return DeviceActions.getNewStateDeviceByCommandId0Failed({ errorMessage: this.translate.instant('ERROR_DEVICE_STATE') });
                    }
                }),
                catchError(error => of(DeviceActions.getNewStateDeviceByCommandId0Failed({ errorMessage: error.message })))
            )
        )
    ));
    // ------------------------- getNewStateDeviceByCommandId0

    // PASSWORD ----------------------------------------------
    passwordVerifySupport$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.passwordVerifySupport),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.passwordVerify(device.Id, action.password)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.passwordVerifySupportSuccess({ supported: resp.Data.Supported, enabled: resp.Data.Enabled, valid: resp.Data.Valid });
                    }
                    else {
                        //return DeviceActions.passwordVerifySupportFailed({ errorMessage: resp.ErrorMessage });
                        return DeviceActions.passwordVerifySupportFailed({ errorMessage: this.translate.instant('ERROR_VERIFY_PASSWORD_AUTOMATION') });
                    }
                }),
                //catchError(error => of(DeviceActions.passwordVerifySupportFailed({ errorMessage: error.message })))
                catchError(error => of(DeviceActions.passwordVerifySupportFailed({ errorMessage: this.translate.instant('ERROR_VERIFY_PASSWORD_AUTOMATION') })))
            )
        )
    ));

    passwordVerify$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.passwordVerify),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.passwordVerify(device.Id, action.password)
            .pipe(
                map(resp => {
                    if (resp.Success && resp.Data.Supported && resp.Data.Enabled && resp.Data.Valid) {
                        return DeviceActions.passwordVerifySuccess({ supported: resp.Data.Supported, enabled: resp.Data.Enabled, valid: resp.Data.Valid });
                    } else if (resp.Success) {
                        return DeviceActions.passwordVerifyInvalid({ supported: resp.Data.Supported, enabled: resp.Data.Enabled, valid: resp.Data.Valid, errorMessage: this.translate.instant('INVALID_PASSWORD') });
                    }
                    else {
                        return DeviceActions.passwordVerifyFailed({ errorMessage: this.translate.instant('INVALID_PASSWORD') });
                    }
                }),
                catchError(error => of(DeviceActions.passwordVerifyFailed({ errorMessage: this.translate.instant('INVALID_PASSWORD') })))
            )
        )
    ));
    passwordChange$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.passwordChange),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.passwordChange(device.Id, action.passwordOld, action.passwordNew)
            .pipe(
                map(resp => {
                    if (resp.Success && resp.Data.Executed) {
                        return DeviceActions.passwordChangeSuccess({ message: this.translate.instant('PASSWORD_CHANGE_SUCCESS') });
                    } else {
                        return DeviceActions.passwordChangeFailed({ errorMessage: this.translate.instant('INVALID_PASSWORD') });
                    }
                }),
                catchError(error => of(DeviceActions.passwordChangeFailed({ errorMessage: this.translate.instant('INVALID_PASSWORD') })))
            )
        )
    ));

    //--------------------------------CONTROL ACCESS
    // GET EXPORT LOG
    getLogThExport$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getLogThExport),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getLogThExport(device.Keycode, action.startDate, action.endDate, action.metrics)
            .pipe(
                map(resp => {
                    if (resp.url != undefined) {
                        return DeviceActions.getLogThExportSuccess({
                            logThExport: {
                                Url: resp.url,
                                FileType: resp.fileType
                            }
                        });
                    }
                    return DeviceActions.getLogThExportFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getLogThExportFailed({ errorMessage: error.message })))
            )
        )
    ));

    // GROUPS
    getGroups$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getGroups),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getGroups(device.Id, action.filter,
            action.sort, action.order, action.size, action.page)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getGroupsSuccess({
                            groups: respData.Groups,
                            paging: respData.Paging, maxData: respData.Counters
                        });
                    }
                    return DeviceActions.getGroupsFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getGroupsFailed({ errorMessage: error.message })))
            )
        )
    ));

    getGroup$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getGroup
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.getGroup(device.Id, action.groupId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const group = resp.Data;
                        return DeviceActions.getGroupSuccess({ group });
                    }
                    return DeviceActions.getGroupFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getGroupFailed({ errorMessage: error.message })))
            )
        )
    ));

    addGroup$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addGroup),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.addGroup(device.Id, action.group)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.addGroupSuccess({ message: this.translate.instant('ADD_GROUP_SUCCESS') });
                    } else {
                        return DeviceActions.addGroupFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        return of(DeviceActions.addGroupFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.addGroupFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    addGroupSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addGroupSuccess),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventDevice({ eventName: 'new_rbeac_group_completed', params: { page_title: 'RBEPLUS | Access Control' } }));
            }, 2000);
        }),
    ), { dispatch: false });

    editGroup$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editGroup),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editGroup(device.Id, action.group)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editGroupSuccess({ message: this.translate.instant('EDIT_GROUP_SUCCESS') });
                    } else {
                        return DeviceActions.editGroupFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        return of(DeviceActions.editGroupFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.editGroupFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    deleteGroup$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteGroup),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.deleteGroup(device.Id, action.groupId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteGroupSuccess({ message: this.translate.instant('DELETE_GROUP_SUCCESS') });
                    } else {
                        return DeviceActions.deleteGroupFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteGroupFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) })))
            )
        )
    ));


    getGroupsAll$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getGroupsAll),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getGroups(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getGroupsAllSuccess({ groups: respData.Groups });
                    }
                    return DeviceActions.getGroupsAllFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getGroupsAllFailed({ errorMessage: error.message })))
            )
        )
    ));


    // MTMA TIMESLICE
    getWeeklySchedules$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getWeeklySchedules),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getWeeklySchedules(device.Id, action.likeName,
            action.sort, action.order, action.size, action.page)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getWeeklySchedulesSuccess({
                            weeklySchedules: respData.WeeklySchedules,
                            paging: respData.Paging, maxData: respData.Counters
                        });
                    }
                    return DeviceActions.getWeeklySchedulesFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getWeeklySchedulesFailed({ errorMessage: error.message })))
            )
        )
    ));

    getWeeklySchedule$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getWeeklySchedule
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.getWeeklySchedule(device.Id, action.weeklyScheduleId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const weeklySchedule = resp.Data;
                        return DeviceActions.getWeeklyScheduleSuccess({ weeklySchedule });
                    }
                    return DeviceActions.getWeeklyScheduleFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getWeeklyScheduleFailed({ errorMessage: error.message })))
            )
        )
    ));

    addWeeklySchedule$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addWeeklySchedule),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.addWeeklySchedule(device.Id, action.weeklySchedule)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.addWeeklyScheduleSuccess({ message: this.translate.instant('ADD_WEEKLY_SCHEDULE_SUCCESS') });
                    } else {
                        return DeviceActions.addWeeklyScheduleFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        return of(DeviceActions.addWeeklyScheduleFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.addWeeklyScheduleFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    addWeeklyScheduleAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addWeeklyScheduleSuccess),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventDevice({ eventName: 'new_rbeac_weeklyschedule_completed', params: { page_title: 'RBEPLUS | Access Control' } }));
            }, 2000);
        }),
    ), { dispatch: false });

    editWeeklySchedule$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editWeeklySchedule),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editWeeklySchedule(device.Id, action.weeklySchedule)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editWeeklyScheduleSuccess({ message: this.translate.instant('EDIT_WEEKLY_SCHEDULE_SUCCESS') });
                    } else {
                        return DeviceActions.editWeeklyScheduleFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        return of(DeviceActions.editWeeklyScheduleFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.editWeeklyScheduleFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    deleteWeeklySchedule$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteWeeklySchedule),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.deleteWeeklySchedule(device.Id, action.weeklyScheduleId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteWeeklyScheduleSuccess({ message: this.translate.instant('DELETE_WEEKLY_SCHEDULE_SUCCESS') });
                    } else {
                        return DeviceActions.deleteWeeklyScheduleFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteWeeklyScheduleFailed({ errorMessage: error.message })))
            )
        )
    ));


    getWeeklySchedulesAll$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getWeeklySchedulesAll),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getWeeklySchedules(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getWeeklySchedulesAllSuccess({ weeklySchedules: respData.WeeklySchedules });
                    }
                    return DeviceActions.getWeeklySchedulesAllFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getWeeklySchedulesAllFailed({ errorMessage: error.message })))
            )
        )
    ));


    // MTMA TIMESLICE
    getSpecialPlannings$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getSpecialPlannings),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getSpecialPlannings(device.Id, action.likeName,
            action.sort, action.order, action.size, action.page)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getSpecialPlanningsSuccess({
                            specialPlannings: respData.SpecialPlannings,
                            paging: respData.Paging, maxData: respData.Counters
                        });
                    }
                    return DeviceActions.getSpecialPlanningsFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getSpecialPlanningsFailed({ errorMessage: error.message })))
            )
        )
    ));

    getSpecialPlanning$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getSpecialPlanning
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.getSpecialPlanning(device.Id, action.specialPlanningId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const specialPlanning = resp.Data;
                        return DeviceActions.getSpecialPlanningSuccess({ specialPlanning });
                    }
                    return DeviceActions.getSpecialPlanningFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getSpecialPlanningFailed({ errorMessage: error.message })))
            )
        )
    ));

    addSpecialPlanning$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addSpecialPlanning),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.addSpecialPlanning(device.Id, action.specialPlanning)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.addSpecialPlanningSuccess({ message: this.translate.instant('ADD_SPECIAL_PLANNING_SUCCESS') });
                    } else {
                        return DeviceActions.addSpecialPlanningFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        return of(DeviceActions.addSpecialPlanningFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.addSpecialPlanningFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    addSpecialPlanningAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addSpecialPlanningSuccess),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventDevice({ eventName: 'new_rbeac_specialplanning_completed', params: { page_title: 'RBEPLUS | Access Control' } }));
            }, 2000);
        }),
    ), { dispatch: false });

    editSpecialPlanning$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editSpecialPlanning),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editSpecialPlanning(device.Id, action.specialPlanning)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editSpecialPlanningSuccess({ message: this.translate.instant('EDIT_SPECIAL_PLANNING_SUCCESS') });
                    } else {
                        return DeviceActions.editSpecialPlanningFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        return of(DeviceActions.editSpecialPlanningFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.editSpecialPlanningFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    deleteSpecialPlanning$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteSpecialPlanning),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.deleteSpecialPlanning(device.Id, action.specialPlanningId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteSpecialPlanningSuccess({ message: this.translate.instant('DELETE_SPECIAL_PLANNING_SUCCESS') });
                    } else {
                        return DeviceActions.deleteSpecialPlanningFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteSpecialPlanningFailed({ errorMessage: error.message })))
            )
        )
    ));


    getSpecialPlanningsAll$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getSpecialPlanningsAll),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getSpecialPlannings(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getSpecialPlanningsAllSuccess({ specialPlannings: respData.SpecialPlannings });
                    }
                    return DeviceActions.getSpecialPlanningsAllFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getSpecialPlanningsAllFailed({ errorMessage: error.message })))
            )
        )
    ));

    //ACINFO SYNC 

    acRbeSync$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.acRbeSync),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.acRbeSync(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.acRbeSyncSuccess({ message: this.translate.instant('AC_RBE_SYNC_SUCCESS') });
                    } else {
                        return DeviceActions.acRbeSyncFailed({ errorMessage: this.translate.instant('AC_RBE_SYNC_FAILED') });
                    }
                }),
                catchError(error => of(DeviceActions.acRbeSyncFailed({ errorMessage: this.translate.instant('AC_RBE_SYNC_FAILED') })))
            )
        )
    ));

    // ACINFO DELETE

    acRbeReset$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.acRbeReset),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.acRbeReset(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.acRbeResetSuccess({ message: this.translate.instant('AC_RBE_RESET_SUCCESS') });
                    } else {
                        return DeviceActions.acRbeResetFailed({ errorMessage: this.translate.instant('AC_RBE_RESET_FAILED') });
                    }
                }),
                catchError(error => of(DeviceActions.acRbeResetFailed({ errorMessage: this.translate.instant('AC_RBE_RESET_FAILED') })))
            )
        )
    ));
    //--------------------------------CONTROL ACCESS

    //--------------------------------Mtma Control Access

    // GROUPS
    getMtmaGroups$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaGroups),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaGroups(device.Id, action.filter,
            action.sort, action.order, action.size, action.page)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaGroupsSuccess({
                            groups: respData.Groups,
                            paging: respData.Paging, maxData: respData.Counters
                        });
                    }
                    return DeviceActions.getMtmaGroupsFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaGroupsFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaGroup$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getMtmaGroup
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaGroup(device.Id, action.groupId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const group = resp.Data;
                        return DeviceActions.getMtmaGroupSuccess({ group });
                    }
                    return DeviceActions.getMtmaGroupFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaGroupFailed({ errorMessage: error.message })))
            )
        )
    ));

    addMtmaGroup$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addMtmaGroup),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.addMtmaGroup(device.Id, action.group)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.addMtmaGroupSuccess({ message: this.translate.instant('ADD_GROUP_SUCCESS') });
                    } else {
                        return DeviceActions.addMtmaGroupFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        if (error.error.ErrorMessage === 'MTMA_GROUPS_NAME_SAME') {
                            return of(DeviceActions.editMtmaGroupFailed({ errorMessage: this.translate.instant('GROUP_NAME_SAME') }));
                        }
                        return of(DeviceActions.addMtmaGroupFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.addMtmaGroupFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    addMtmaGroupSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addMtmaGroup),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventDevice({ eventName: 'new_mtmaac_group_completed', params: { page_title: 'MTMA | Access Control' } }));
            }, 2000);
        }),
    ), { dispatch: false });

    editMtmaGroup$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editMtmaGroup),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editMtmaGroup(device.Id, action.group)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editMtmaGroupSuccess({ message: this.translate.instant('EDIT_GROUP_SUCCESS') });
                    } else {
                        return DeviceActions.editMtmaGroupFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        if (error.error.ErrorMessage === 'MTMA_GROUPS_NAME_SAME') {
                            return of(DeviceActions.editMtmaGroupFailed({ errorMessage: this.translate.instant('GROUP_NAME_SAME') }));
                        }
                        return of(DeviceActions.editMtmaGroupFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.editMtmaGroupFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    deleteMtmaGroup$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteMtmaGroup),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.deleteMtmaGroup(device.Id, action.groupId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteMtmaGroupSuccess({ message: this.translate.instant('DELETE_GROUP_SUCCESS') });
                    } else {
                        return DeviceActions.deleteMtmaGroupFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteMtmaGroupFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) })))
            )
        )
    ));


    getMtmaGroupsAll$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaGroupsAll),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaGroups(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaGroupsAllSuccess({ groups: respData.Groups });
                    }
                    return DeviceActions.getMtmaGroupsAllFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaGroupsAllFailed({ errorMessage: error.message })))
            )
        )
    ));


    // MTMA TIMESLICE
    getMtmaWeeklySchedules$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaWeeklySchedules),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaWeeklySchedules(device.Id, action.likeName,
            action.sort, action.order, action.size, action.page)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaWeeklySchedulesSuccess({
                            weeklySchedules: respData.WeeklySchedules,
                            paging: respData.Paging, maxData: respData.Counters
                        });
                    }
                    return DeviceActions.getMtmaWeeklySchedulesFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaWeeklySchedulesFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaWeeklySchedule$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getMtmaWeeklySchedule
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaWeeklySchedule(device.Id, action.weeklyScheduleId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const weeklySchedule = resp.Data;
                        return DeviceActions.getMtmaWeeklyScheduleSuccess({ weeklySchedule });
                    }
                    return DeviceActions.getMtmaWeeklyScheduleFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaWeeklyScheduleFailed({ errorMessage: error.message })))
            )
        )
    ));

    addMtmaWeeklySchedule$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addMtmaWeeklySchedule),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.addMtmaWeeklySchedule(device.Id, action.weeklySchedule)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.addMtmaWeeklyScheduleSuccess({ message: this.translate.instant('ADD_WEEKLY_SCHEDULE_SUCCESS') });
                    } else {
                        return DeviceActions.addMtmaWeeklyScheduleFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        return of(DeviceActions.addMtmaWeeklyScheduleFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.addMtmaWeeklyScheduleFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    addMtmaWeeklyScheduleSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addMtmaWeeklySchedule),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventDevice({ eventName: 'new_mtmaac_weeklyschedule_completed', params: { page_title: 'MTMA | Access Control' } }));
            }, 2000);
        }),
    ), { dispatch: false });

    editMtmaWeeklySchedule$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editMtmaWeeklySchedule),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editMtmaWeeklySchedule(device.Id, action.weeklySchedule)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editMtmaWeeklyScheduleSuccess({ message: this.translate.instant('EDIT_WEEKLY_SCHEDULE_SUCCESS') });
                    } else {
                        return DeviceActions.editMtmaWeeklyScheduleFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        return of(DeviceActions.editMtmaWeeklyScheduleFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.editMtmaWeeklyScheduleFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    deleteMtmaWeeklySchedule$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteMtmaWeeklySchedule),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.deleteMtmaWeeklySchedule(device.Id, action.weeklyScheduleId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteMtmaWeeklyScheduleSuccess({ message: this.translate.instant('DELETE_WEEKLY_SCHEDULE_SUCCESS') });
                    } else {
                        return DeviceActions.deleteMtmaWeeklyScheduleFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteMtmaWeeklyScheduleFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaWeeklySchedulesAll$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaWeeklySchedulesAll),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaWeeklySchedules(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaWeeklySchedulesAllSuccess({ weeklySchedules: respData.WeeklySchedules });
                    }
                    return DeviceActions.getMtmaWeeklySchedulesAllFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaWeeklySchedulesAllFailed({ errorMessage: error.message })))
            )
        )
    ));

    // MTMA TIMESLICE
    getMtmaSpecialPlannings$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaSpecialPlannings),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaSpecialPlannings(device.Id, action.likeName,
            action.sort, action.order, action.size, action.page)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaSpecialPlanningsSuccess({
                            specialPlannings: respData.SpecialPlannings,
                            paging: respData.Paging, maxData: respData.Counters
                        });
                    }
                    return DeviceActions.getMtmaSpecialPlanningsFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaSpecialPlanningsFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaSpecialPlanning$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getMtmaSpecialPlanning
        ),
        withLatestFrom(
            this.store.select('device', 'currentDevice')
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaSpecialPlanning(device.Id, action.specialPlanningId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const specialPlanning = resp.Data;
                        return DeviceActions.getMtmaSpecialPlanningSuccess({ specialPlanning });
                    }
                    return DeviceActions.getMtmaSpecialPlanningFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaSpecialPlanningFailed({ errorMessage: error.message })))
            )
        )
    ));

    addMtmaSpecialPlanning$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addMtmaSpecialPlanning),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.addMtmaSpecialPlanning(device.Id, action.specialPlanning)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.addMtmaSpecialPlanningSuccess({ message: this.translate.instant('ADD_SPECIAL_PLANNING_SUCCESS') });
                    } else {
                        return DeviceActions.addMtmaSpecialPlanningFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        return of(DeviceActions.addMtmaSpecialPlanningFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.addMtmaSpecialPlanningFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    addMtmaSpecialPlanningSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.addMtmaSpecialPlanning),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventDevice({ eventName: 'new_mtmaac_specialplanning_completed', params: { page_title: 'MTMA | Access Control' } }));
            }, 2000);
        }),
    ), { dispatch: false });

    editMtmaSpecialPlanning$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editMtmaSpecialPlanning),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editMtmaSpecialPlanning(device.Id, action.specialPlanning)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editMtmaSpecialPlanningSuccess({ message: this.translate.instant('EDIT_SPECIAL_PLANNING_SUCCESS') });
                    } else {
                        return DeviceActions.editMtmaSpecialPlanningFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        return of(DeviceActions.editMtmaSpecialPlanningFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.editMtmaSpecialPlanningFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    deleteMtmaSpecialPlanning$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.deleteMtmaSpecialPlanning),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.deleteMtmaSpecialPlanning(device.Id, action.specialPlanningId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.deleteMtmaSpecialPlanningSuccess({ message: this.translate.instant('DELETE_SPECIAL_PLANNING_SUCCESS') });
                    } else {
                        return DeviceActions.deleteMtmaSpecialPlanningFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(DeviceActions.deleteMtmaSpecialPlanningFailed({ errorMessage: error.message })))
            )
        )
    ));

    getMtmaSpecialPlanningsAll$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaSpecialPlanningsAll),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaSpecialPlannings(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaSpecialPlanningsAllSuccess({ specialPlannings: respData.SpecialPlannings });
                    }
                    return DeviceActions.getMtmaSpecialPlanningsAllFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaSpecialPlanningsAllFailed({ errorMessage: error.message })))
            )
        )
    ));

    //--------------------------------Mtma Control Access

    //--------------------------------Settings Paging
    getDeviceSettingsPaging$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getDeviceSettingsPaging),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
            this.store.select('user', 'currentLang'),
        ),
        switchMap(([action, device, lang]) => this.deviceService.getDeviceSettingsPaging(device.Id, lang.code, action.page)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.getDeviceSettingsPagingSuccess({ SettingsCategoriesPaging: resp.Data });
                    } else {
                        return DeviceActions.getDeviceSettingsPagingFailed({ errorMessage: resp.ErrorMessage });
                    }

                }),
                catchError(error => of(DeviceActions.getDeviceSettingsPagingFailed({ errorMessage: error.message })))
            )
        )
    ));
    //--------------------------------Settings Paging

    //--------------------------------TH LOG
    getThLog$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getThLog),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getThLog(device.Keycode, action.from, action.intervaltype, action.location)
            .pipe(
                map(resp => {
                    if (resp.Keycode !== undefined) {
                        return DeviceActions.getThLogSuccess({
                            thermoLog: resp,
                        });
                    }
                    return DeviceActions.getThLogFailed({ errorMessage: 'error' })
                }),
                catchError(error => of(DeviceActions.getThLogFailed({ errorMessage: 'error' })))
            )
        )
    ));
    //--------------------------------TH LOG

    //---------------UPDATE DEVICE RTC

    updateDeviceAutomationRtc$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.updateDeviceAutomationRtc),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.updateDeviceAutomationRtc(device.Id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.updateDeviceAutomationRtcSuccess({ message: this.translate.instant('UPDATE_DEVICE_RTC_SUCCESS') });
                    } else {
                        return DeviceActions.updateDeviceAutomationRtcFailed({ errorMessage: this.translate.instant('UPDATE_DEVICE_RTC_DEVICES_FAILED') });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(DeviceActions.updateDeviceAutomationRtcFailed({ errorMessage: this.translate.instant('UPDATE_DEVICE_RTC_DEVICES_FAILED') }));
                })
            ))
    ));

    //---------------UPDATE DEVICE RTC


    getAutomationsFirmwares$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getAutomationsFirmwares),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getAutomationsFirmwares(action.test)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getAutomationsFirmwaresSuccess({ automationsFirmwaresList: respData });
                    }
                    return DeviceActions.getAutomationsFirmwaresFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getAutomationsFirmwaresFailed({ errorMessage: error.message })))
            )
        )
    ));


    getAutomationsAlias$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getAutomationsAlias),
        concatMap(action => of(action).pipe(
            withLatestFrom(
                this.store.select('device', 'currentDevice'),
            ),
        )),
        switchMap(([, device]) => {
            const deviceId = device.Id;
            return this.deviceService.getAutomationsAlias(deviceId)
                .pipe(
                    map(resp => {
                        if (resp.Success) {
                            return DeviceActions.getAutomationsAliasSuccess({ automationsAlias: resp.Data });
                        } else {
                            return DeviceActions.getAutomationsAliasFailed({ errorMessage: resp.ErrorMessage });
                        }

                    }),
                    catchError(error => of(DeviceActions.getAutomationsAliasFailed({ errorMessage: error.message })))
                );
        })
    ));


    editAutomationsAlias$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editAutomationsAlias),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        mergeMap(([action, device]) => this.deviceService.editAutomationsAlias(device.Id, action.automationsAlias)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return DeviceActions.editAutomationsAliasSuccess({ message: this.translate.instant('EDIT_AUTOMATION_ALIAS_SUCCESS') });
                    } else {
                        return DeviceActions.editAutomationsAliasFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined) {
                        return of(DeviceActions.editAutomationsAliasFailed({ errorMessage: this.translate.instant(error.error.ErrorMessage) }));
                    } else {
                        return of(DeviceActions.editAutomationsAliasFailed({ errorMessage: error.message }));
                    }
                })
            )
        )
    ));

    getAutomationsText$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getAutomationsText),
        withLatestFrom(this.store.select('user', 'currentLang')),
        switchMap(([, lang]) => {
            const language = lang.code.slice(0, 2);
            return this.deviceService.getAutomationsText(language)
                .pipe(
                    map(resp => {
                        if (resp) {
                            return DeviceActions.getAutomationsTextSuccess({ automationsText: resp });
                        } else {
                            return DeviceActions.getAutomationsTextFailed({ errorMessage: resp['ErrorMessage'] });
                        }

                    }),
                    catchError(error => of(DeviceActions.getAutomationsAliasFailed({ errorMessage: error.message })))
                );
        })
    ));


    getMtmaPermissions$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.getMtmaPermissions),
        withLatestFrom(
            this.store.select('device', 'currentDevice'),
        ),
        switchMap(([action, device]) => this.deviceService.getMtmaPermissions(device.Id, action.filter,
            action.sort, action.order, action.size, action.page)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const respData = resp.Data;
                        return DeviceActions.getMtmaPermissionsSuccess({
                            permissions: respData.Permissions,
                            paging: respData.Paging, maxData: respData.Counters
                        });
                    }
                    return DeviceActions.getMtmaPermissionsFailed({ errorMessage: resp.ErrorMessage });
                }),
                catchError(error => of(DeviceActions.getMtmaPermissionsFailed({ errorMessage: error.message })))
            )
        )
    ));

    // NOTIFY
    notifyCustom$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.deviceStateUpdateSuccess
        ),
        tap((action) => {
            const notification = new Notification("DEVICE", action.message, action.notificationLevel || NotificationLevel.Info, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, listOnly: true }));
        })
    ), { dispatch: false });

    notifyWarn$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.getBoardusersInfoSuccess,
        ),
        tap((action) => {
            if (action.message === '') {
                return;
            }
            const notification = new Notification("DEVICE", action.message, NotificationLevel.Warning, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    notifySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.addDeviceSuccess,
            DeviceActions.editDeviceSuccess,
            DeviceActions.deleteDeviceSuccess,
            DeviceActions.shareDeviceSuccess,
            DeviceActions.acquireBoardUserSuccess,
            DeviceActions.deleteSharedDeviceSuccess,
            DeviceActions.editCommandsSuccess,
            DeviceActions.addMtmaModuleSuccess,
            DeviceActions.editMtmaModuleSuccess,
            DeviceActions.editParamMtmaModuleSuccess,
            DeviceActions.deleteMtmaModuleSuccess,
            DeviceActions.addMtmaContactSuccess,
            DeviceActions.editMtmaContactSuccess,
            DeviceActions.deleteMtmaContactSuccess,
            DeviceActions.addMtmaUnitSuccess,
            DeviceActions.editMtmaUnitSuccess,
            DeviceActions.deleteMtmaUnitSuccess,
            DeviceActions.addMtmaDtmfSuccess,
            DeviceActions.editMtmaDtmfSuccess,
            DeviceActions.deleteMtmaDtmfSuccess,
            DeviceActions.addMtmaTagSuccess,
            DeviceActions.editMtmaTagSuccess,
            DeviceActions.deleteMtmaTagSuccess,
            DeviceActions.addMtmaKeypadSuccess,
            DeviceActions.editMtmaKeypadSuccess,
            DeviceActions.deleteMtmaKeypadSuccess,
            DeviceActions.addMtmaTimeSliceSuccess,
            DeviceActions.editMtmaTimeSliceSuccess,
            DeviceActions.deleteMtmaTimeSliceSuccess,
            DeviceActions.addMtmaRuleSuccess,
            DeviceActions.editMtmaRuleSuccess,
            DeviceActions.deleteMtmaRuleSuccess,
            DeviceActions.editMtmaRulesAccessSuccess,
            DeviceActions.cloneMtmaSuccess,
            DeviceActions.postFirmwareUpdateSuccess,
            DeviceActions.initDeviceSuccess,
            DeviceActions.addBoardUserSuccess,
            DeviceActions.editBoardUserSuccess,
            DeviceActions.executeCommandSuccess,
            DeviceActions.getAutomationDiscoverSuccess,
            DeviceActions.addDeviceTimerSuccess,
            DeviceActions.editDeviceTimerSuccess,
            DeviceActions.deleteDeviceTimerSuccess,
            DeviceActions.deleteDeviceAllTimerSuccess,
            DeviceActions.addDeviceSpecialDaySuccess,
            DeviceActions.editDeviceSpecialDaySuccess,
            DeviceActions.deleteDeviceSpecialDaySuccess,
            DeviceActions.deleteDeviceAllSpecialDaySuccess,
            DeviceActions.editNameAutomationConnectedSuccess,
            DeviceActions.editMultiIOSuccess,
            DeviceActions.updateFirmwareVersion,
            DeviceActions.passwordChangeSuccess,
            DeviceActions.updateUserProfileSuccess,
            DeviceActions.addWeeklyScheduleSuccess,
            DeviceActions.editWeeklyScheduleSuccess,
            DeviceActions.deleteWeeklyScheduleSuccess,
            DeviceActions.addSpecialPlanningSuccess,
            DeviceActions.editSpecialPlanningSuccess,
            DeviceActions.deleteSpecialPlanningSuccess,
            DeviceActions.addGroupSuccess,
            DeviceActions.editGroupSuccess,
            DeviceActions.deleteGroupSuccess,
            DeviceActions.toggleRemoteClientLockSuccess,
            DeviceActions.deleteRemoteClientSuccess,
            DeviceActions.changeMaxSlotsSuccess,
            DeviceActions.acRbeSyncSuccess,
            DeviceActions.acRbeResetSuccess,
            DeviceActions.deleteBoardUserSuccess,
            DeviceActions.deleteAllBoardUsersSuccess,
            DeviceActions.addMtmaSpecialPlanningSuccess,
            DeviceActions.editMtmaSpecialPlanningSuccess,
            DeviceActions.deleteMtmaSpecialPlanningSuccess,
            DeviceActions.addMtmaWeeklyScheduleSuccess,
            DeviceActions.editMtmaWeeklyScheduleSuccess,
            DeviceActions.deleteMtmaWeeklyScheduleSuccess,
            DeviceActions.addMtmaGroupSuccess,
            DeviceActions.editMtmaGroupSuccess,
            DeviceActions.deleteMtmaGroupSuccess,
            DeviceActions.updateDeviceAutomationRtcSuccess,
            DeviceActions.deleteBoardUserSelectedSuccess,
            DeviceActions.editAutomationsAliasSuccess,
        ),
        tap((action) => {
            if (action.message === '') {
                return;
            }
            const notification = new Notification("DEVICE", action.message, NotificationLevel.Success, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    notifyError$ = createEffect(() => this.actions$.pipe(
        ofType(
            DeviceActions.addDeviceFailed,
            DeviceActions.acquireBoardUserFailed,
            DeviceActions.getCurrentDeviceFailed,
            DeviceActions.shareDeviceFailed,
            DeviceActions.deleteSharedDeviceFailed,
            DeviceActions.editCommandsFailed,
            DeviceActions.confirmShareDeviceFailed,
            DeviceActions.getMtmaModulesFailed,
            DeviceActions.getMtmaModuleFailed,
            DeviceActions.addMtmaModuleFailed,
            DeviceActions.editMtmaModuleFailed,
            DeviceActions.editParamMtmaModuleFailed,
            DeviceActions.deleteMtmaModuleFailed,
            DeviceActions.getMtmaActionsFailed,
            DeviceActions.getMtmaContactsFailed,
            DeviceActions.getMtmaContactsAllFailed,
            DeviceActions.getMtmaContactFailed,
            DeviceActions.addMtmaContactFailed,
            DeviceActions.editMtmaContactFailed,
            DeviceActions.deleteMtmaContactFailed,
            DeviceActions.getMtmaUnitsFailed,
            DeviceActions.getMtmaUnitFailed,
            DeviceActions.addMtmaUnitFailed,
            DeviceActions.editMtmaUnitFailed,
            DeviceActions.deleteMtmaUnitFailed,
            DeviceActions.getMtmaDtmfsFailed,
            DeviceActions.getMtmaDtmfFailed,
            DeviceActions.addMtmaDtmfFailed,
            DeviceActions.editMtmaDtmfFailed,
            DeviceActions.deleteMtmaDtmfFailed,
            DeviceActions.getMtmaTagsFailed,
            DeviceActions.getMtmaTagFailed,
            DeviceActions.addMtmaTagFailed,
            DeviceActions.editMtmaTagFailed,
            DeviceActions.deleteMtmaTagFailed,
            DeviceActions.getMtmaTagsAllFailed,
            DeviceActions.getMtmaKeypadsFailed,
            DeviceActions.getMtmaKeypadFailed,
            DeviceActions.addMtmaKeypadFailed,
            DeviceActions.editMtmaKeypadFailed,
            DeviceActions.deleteMtmaKeypadFailed,
            DeviceActions.getMtmaKeypadsAllFailed,
            DeviceActions.getMtmaSettingsModulesFailed,
            DeviceActions.getMtmaChangesFailed,
            DeviceActions.getMtmaActiveTaskFailed,
            DeviceActions.mtmaWriteConfigFailed,
            DeviceActions.getMtmaAbortFailed,
            DeviceActions.getMtmaTimeSlicesFailed,
            DeviceActions.getMtmaTimeSliceFailed,
            DeviceActions.addMtmaTimeSliceFailed,
            DeviceActions.editMtmaTimeSliceFailed,
            DeviceActions.deleteMtmaTimeSliceFailed,
            DeviceActions.getMtmaRulesFailed,
            DeviceActions.getMtmaRuleFailed,
            DeviceActions.addMtmaRuleFailed,
            DeviceActions.editMtmaRuleFailed,
            DeviceActions.deleteMtmaRuleFailed,
            DeviceActions.cloneMtmaFailed,
            DeviceActions.editMtmaRulesAccessFailed,
            DeviceActions.getDatetimeDeviceFailed,
            DeviceActions.getFirmwareUpdateFailed,
            DeviceActions.getGWFirmwareUpdateFailed,
            DeviceActions.postFirmwareUpdateFailed,
            DeviceActions.initDeviceFailed,
            DeviceActions.addBoardUserFailed,
            DeviceActions.editBoardUserFailed,
            DeviceActions.getDeviceSettingsFailed,
            DeviceActions.getAutomationDiscoverFailed,
            DeviceActions.addDeviceTimerFailed,
            DeviceActions.editDeviceTimerFailed,
            DeviceActions.deleteDeviceTimerFailed,
            DeviceActions.deleteDeviceAllTimerFailed,
            DeviceActions.addDeviceSpecialDayFailed,
            DeviceActions.editDeviceSpecialDayFailed,
            DeviceActions.deleteDeviceSpecialDayFailed,
            DeviceActions.deleteDeviceAllSpecialDayFailed,
            DeviceActions.editNameAutomationConnectedFailed,
            DeviceActions.editMultiIOFailed,
            DeviceActions.getRbeAccessLogFailed,
            DeviceActions.getRbeAccessLogExportFailed,
            DeviceActions.getAccessLogFailed,
            DeviceActions.executeCommandFailed,
            DeviceActions.syncBoardUsersFailed,
            DeviceActions.passwordVerifyFailed,
            DeviceActions.passwordChangeFailed,
            DeviceActions.passwordVerifyInvalid,
            DeviceActions.passwordVerifySupportFailed,
            DeviceActions.updateUserProfileFailed,
            DeviceActions.getGroupsFailed,
            DeviceActions.getGroupFailed,
            DeviceActions.getGroupsAllFailed,
            DeviceActions.addGroupFailed,
            DeviceActions.editGroupFailed,
            DeviceActions.deleteGroupFailed,
            DeviceActions.getWeeklySchedulesFailed,
            DeviceActions.getWeeklyScheduleFailed,
            DeviceActions.getWeeklySchedulesAllFailed,
            DeviceActions.addWeeklyScheduleFailed,
            DeviceActions.editWeeklyScheduleFailed,
            DeviceActions.deleteWeeklyScheduleFailed,
            DeviceActions.getSpecialPlanningsFailed,
            DeviceActions.getSpecialPlanningFailed,
            DeviceActions.getSpecialPlanningsAllFailed,
            DeviceActions.addSpecialPlanningFailed,
            DeviceActions.editSpecialPlanningFailed,
            DeviceActions.deleteSpecialPlanningFailed,
            DeviceActions.toggleRemoteClientLockFailed,
            DeviceActions.deleteRemoteClientFailed,
            DeviceActions.changeMaxSlotsFailed,
            DeviceActions.getCommandsFailed,
            DeviceActions.getMtmaAutomationCommandsFailed,
            DeviceActions.deleteAllBoardUsersFailed,
            DeviceActions.acRbeSyncFailed,
            DeviceActions.acRbeResetFailed,
            DeviceActions.getDeviceSettingsPagingFailed,
            DeviceActions.deleteBoardUserFailed,
            DeviceActions.deleteAllBoardUsersFailed,
            DeviceActions.addMtmaSpecialPlanningFailed,
            DeviceActions.editMtmaSpecialPlanningFailed,
            DeviceActions.deleteMtmaSpecialPlanningFailed,
            DeviceActions.addMtmaWeeklyScheduleFailed,
            DeviceActions.editMtmaWeeklyScheduleFailed,
            DeviceActions.deleteMtmaWeeklyScheduleFailed,
            DeviceActions.addMtmaGroupFailed,
            DeviceActions.editMtmaGroupFailed,
            DeviceActions.deleteMtmaGroupFailed,
            DeviceActions.getMtmaGroupFailed,
            DeviceActions.getMtmaSpecialPlanningFailed,
            DeviceActions.getMtmaWeeklyScheduleFailed,
            DeviceActions.getMtmaGroupsFailed,
            DeviceActions.getMtmaSpecialPlanningsFailed,
            DeviceActions.getMtmaWeeklySchedulesFailed,
            DeviceActions.getDeviceSettingsPagingFailed,
            DeviceActions.updateDeviceAutomationRtcFailed,
            DeviceActions.deleteBoardUserSelectedFailed,
            DeviceActions.getAutomationsAliasFailed,
            DeviceActions.editAutomationsAliasFailed,
            DeviceActions.getAutomationsTextFailed,
            DeviceActions.getMtmaPermissionsFailed,
        ),
        tap((action) => {
            if (action.errorMessage === '') {
                return;
            }
            const notification = new Notification("DEVICE", action.errorMessage, NotificationLevel.Error, '');
            if (notification.message === 'device not ready (configured != 0)') {
                notification.message = this.translate.instant('DEVICE_NOT_CONFIGURED')
            }
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    constructor(
        private actions$: Actions,
        private router: Router,
        private deviceService: DeviceService,
        private userService: UserService,
        private boardUsersService: BoardUsersService,
        private profilesService: ProfilesService,
        private translate: TranslateService,
        private store: Store<AppState>,
    ) { }
}
